import { relatedNoteReasons } from '../core/note-relations.js';
import { DEFAULT_TAG_CATEGORY_ID } from '../core/tag-filter.js';
import { MAX_ROUTINE_SECONDS } from '../core/routine-duration.js';
import { routineListItems, mergeTaskList, setOccurrenceDone } from './routine-list.js';
import { inProgressTags, statusFromTags } from '../core/node-tags.js';
import { assertDocQuota, isDocQuotaBlocked } from "../services/quota-state.js";
import { isMapFull } from "../services/map-size.js";
import { mayHaveOtherMembers } from "../services/map-members.js";
/**
 * CRDT Bridge - CRDT-First Architecture for Real-Time Sync
 *
 * This module provides the primary API for all document mutations.
 * CRDT (via Yjs) is the source of truth; Store is a derived view.
 *
 * Architecture (CRDT-First, Post-Phase 5):
 *   1. All writes go through CRDTBridge API (addNode, updateNode, etc.)
 *   2. CRDT writes trigger observer → synchronous Store rebuild
 *   3. Store subscribers update views with consistent state
 *   4. Changes sync to server via WebSocket automatically
 *
 * Data Flow:
 *   Write: CRDTBridge.addNode() → CRDT → Observer → Store._setDataFromCRDT() → Views
 *   Sync:  Server → WebSocket → CRDT → Observer → Store._setDataFromCRDT() → Views
 *
 * Key Features:
 *   - Batch mode: Buffer multiple ops, apply as single CRDT transaction
 *   - Event timing: Events emitted after Store rebuild (consistent state)
 *   - Shared docs: Separate CRDT docs for shared branches
 *   - DocNotes: Separate from tree, doesn't trigger Store rebuilds
 */

import { reportSyncPause } from './sync-pause.js';
import { markLinkedOffline, clearLinkedOffline, isLinkedOffline, resetLinkedOffline } from './linked-offline.js';
// PLAN23 Fix: Removed 'off' which doesn't exist in events.js
import { on, emit, Events } from "../core/events.js";
import { t } from "../../shared/i18n/index.js";
import { validateTree } from "../core/validation.js";
import { createLogger } from "../core/logger.js";
import {
  getPerformanceMetrics,
  resetPerformanceMetrics,
} from "./crdt-metrics.js";
// The open map's state (engine/map-state.js), passed to the modules below as
// their "store": the interface's Store follows it (Phase 5b, step 3).
import * as store from "../map-state.js";
import { YjsDocManager } from "./yjs-doc.js";
import { SyncManager, ConnectionState } from "./websocket.js";
import * as LinkAccess from "./link-access.js";
import { VaultStorage, setBeforeDocCacheDelete } from "../crypto/vault-storage.js";
import { Keypair } from "../crypto/keypair.js";
import { notify, ask, newItemColor } from "../host.js";
import { NOTICE, QUESTION } from "../notices.js";
import * as AccessRequests from "./access-requests.js";
import * as ExecutionMap from "./execution-map.js";
import * as TaskPostponements from "./task-postponements.js";
import * as Routines from './routines.js';
import { dayKey, validDay } from '../core/recurrence.js';
import { computeExecutionStats } from "./execution-stats.js";
import * as Y from "yjs";
import { setLastOpenedDoc } from "../../shared/local-doc-state.js";
import {
  getActiveShareId,
  setActiveShareId,
  setShareRole,
  getShareRole,
  removeShareRole,
  getActiveShareRole,
  isActiveShareReadOnly,
  isActiveShareDeleteBlocked,
  setMainDocIdResolver,
} from "./share-state.js";
import {
  clearAllLocalDimensions,
} from "./dimension-manager.js";
import {
  initShareLifecycle,
  initSharedDoc as _initSharedDocRaw,
  stopSharedDocSync,
  stopAllSharedDocSync,
  getSharedDocManager,
  getSharedSyncManager,
  waitForSharedSync,
  setActiveShare,
  getActiveShare,
  getActiveSharedDocs,
  getSharedDocManagersSize,
  clearAllDebounceTimers,
} from "./share-lifecycle.js";
import {
  initStoreRebuild,
  rebuildStoreFromCRDT,
  cancelScheduledRebuild,
  setupCRDTObserver,
  getMainDocObserverCleanup,
  setMainDocObserverCleanup,
  resetCorruptionState,
  flushNotesRefresh,
} from "./store-rebuild.js";
import {
  initNodeOperations,
  generateNodeId,
  addNode,
  pasteNode,
  updateNode,
  removeNode,
  moveNode as moveNodeOp,
} from "./node-operations.js";
import {
  initBatchOperations,
  beginBatch,
  endBatch,
  isBatching,
  resetBatchState,
  getBatchBuffer,
} from "./batch-operations.js";
import {
  initShareRevocation,
  performShareRevocation,
  performDocumentRevocation,
  recoverRevokedShare,
  clearProcessedRevocations,
  isShareRevoked,
  destroy as destroyShareRevocation,
} from "./share-revocation.js";
import { USER_ORIGIN, MIGRATION_ORIGIN } from "./origins.js";
import { unsyncedState, changeMark } from "./unsynced-changes.js";
import { forgetOldCopyBackups } from "./migration-backups.js";
import { ensureNotesSerializer } from "./notes-serializer-facade.js";
import * as undoManager from "../core/undo-manager.js";
import * as StorageHealth from "./storage-health.js";
import { fragmentIsMarkdownOnly } from "./notes-serializer-facade.js";
import { noteDay } from "../core/execution-list.js";
import {
  deleteDocCache,
  clearAccountCaches,
} from "../storage/index.js";

// Re-export share state functions for backward compatibility
export { getActiveShareRole, isActiveShareReadOnly };

// The usual Store rebuild (with linked content): the document loader runs it
// after setting a map's raw tree.
export { rebuildStoreFromCRDT };

// Re-export node operations for backward compatibility (moveNode is below)
export { generateNodeId, addNode, pasteNode, updateNode, removeNode };

// Re-export share lifecycle functions
export {
  stopSharedDocSync,
  stopAllSharedDocSync,
  getSharedDocManager,
  getSharedSyncManager,
  waitForSharedSync,
  setActiveShare,
  getActiveShare,
  getActiveSharedDocs,
};

// Wrapped initSharedDoc that ensures callbacks are initialized
// This handles the shared-map-first scenario where init() hasn't been called
export async function initSharedDoc(shareId, encryptionKey, options = {}) {
  // Ensure minimal initialization before shared doc operations
  ensureMinimalInit();
  return _initSharedDocRaw(shareId, encryptionKey, options);
}

// Re-export batch operations for backward compatibility
export { beginBatch, endBatch, isBatching, resetBatchState };

// Re-export share revocation functions for backward compatibility
export { performShareRevocation, recoverRevokedShare, clearProcessedRevocations, destroyShareRevocation };

// ============================================
// F5.3: Log Level Controls (using shared logger)
// ============================================
const log = createLogger("CRDT Bridge");

// Convenience aliases for internal use
const logError = (...args) => log.error(...args);
const logWarn = (...args) => log.warn(...args);
const logInfo = (...args) => log.info(...args);
const logDebug = (...args) => log.debug(...args);

/**
 * Set the log level for CRDT Bridge
 * @param {number} level - 0=none, 1=error, 2=warn, 3=info, 4=debug
 */
export function setLogLevel(level) {
  log.setLevel(level);
}

/**
 * Get the current log level
 * @returns {number}
 */
export function getLogLevel() {
  return log.getLevel();
}

// Re-export metrics functions for backward compatibility
export { getPerformanceMetrics, resetPerformanceMetrics };

// Module state
let docManager = null;
let syncManager = null;
// Edit guards (share-state) check the main document's role, not only an
// active share view's.
setMainDocIdResolver(() => docManager?.docId || null);
let isEnabled = false;
let currentEncryptionKey = null; // Store encryption key for reconnect after import
let syncingDepth = 0; // Counter to prevent CRDT observer from triggering during bulk operations (loadFromServer, syncFullTree, etc.). Counter-based approach handles nested/concurrent operations safely.

// CX-M4 Fix (PLAN21): Track pending docNotes update during syncingDepth > 0
let pendingDocNotesUpdate = false;

/**
 * Increment syncingDepth to suppress CRDT→Store echo during programmatic writes
 */
function incrementSyncingDepth() {
  syncingDepth++;
}

/**
 * Safely decrement syncingDepth with underflow guard
 */
function decrementSyncingDepth() {
  if (syncingDepth > 0) {
    syncingDepth--;
  } else {
    logWarn('syncingDepth underflow detected - possible unbalanced sync operation');
  }

  // CX-M4 Fix (PLAN21): Emit pending docNotes update when sync completes
  // Also check docNotesUpdateDepth to avoid conflicting emissions during local operations
  if (syncingDepth === 0 && pendingDocNotesUpdate && docNotesUpdateDepth === 0) {
    pendingDocNotesUpdate = false;
    logDebug("Emitting queued docNotes change after sync completed");
    emit(Events.DOC_NOTES_CHANGED, { origin: 'remote' });
  }
}
// Phase 2.10 (PLAN17): Track event subscriptions for cleanup
// Most events are managed by CRDT observers, not the central event system.
// This array tracks any event subscriptions that need cleanup during destroy().
// Note: The REQUEST_SHARE_REVOCATION subscription at module bottom is permanent
// (needed to break circular dependency) and doesn't need tracking.
let eventUnsubscribers = [];
// PLAN23 Fix: Removed dead code 'subscribeToEvent' function (was never called)
let isDocumentLoadingCallback = null; // Callback to check if main.js is loading a document
let hasPendingRemoteUpdate = false; // Track if remote update arrived during loading
let postSyncCallbacks = []; // Callbacks to run after initial sync completes (SYNCED state)

// Track which Y.Map keys are node-related vs metadata-related
// Used to filter docNotes-only updates from triggering Store rebuilds
const NODE_MAP_NAME = "nodes";
const METADATA_MAP_NAME = "metadata";
const DOC_NOTES_ARRAY_NAME = "docNotes";

// CR6 Fix: Track docNotes-only updates with a depth counter instead of a boolean flag
// This prevents race conditions when multiple docNotes operations are nested or interleaved
let docNotesUpdateDepth = 0;
// Same idea for execution-map writes (send / remove / tick / pin / reorder).
let executionUpdateDepth = 0;

// ============================================
// Linked Document Registry (reference nodes / symlinks)
// ============================================

/**
 * Registry of linked document sync channels.
 * Maps docId -> { docManager: YjsDocManager, syncManager: SyncManager, dek: CryptoKey, observerCleanup: Function }
 */
const linkedDocRegistry = new Map();


/**
 * Check if a document is currently being loaded
 * This prevents Store updates during document transitions
 */
function isDocumentLoading() {
  return isDocumentLoadingCallback ? isDocumentLoadingCallback() : false;
}


/**
 * Get the appropriate doc manager for a node
 * @param {string} nodeId - The node ID
 * @returns {{ docManager: YjsDocManager|null, shareId: string|null }}
 */
function getDocManagerForNode(nodeId) {
  // If we're in a dedicated share view, use that
  const activeShare = getActiveShareId();
  if (activeShare) {
    return {
      docManager: getSharedDocManager(activeShare),
      shareId: activeShare,
    };
  }

  // Default to main doc
  return { docManager, shareId: null };
}

// Track if minimal initialization has been done (for shared-map-first scenarios)
let minimalInitDone = false;

/**
 * Ensure minimal initialization for modules that need callbacks
 * This is called automatically before shared doc operations when
 * full init() hasn't been called (e.g., opening a shared map as first action)
 *
 * This is idempotent - safe to call multiple times.
 */
export function ensureMinimalInit() {
  if (minimalInitDone) return;

  logDebug("Performing minimal initialization for shared-map-first scenario");

  // Initialize share lifecycle manager with callbacks
  initShareLifecycle({
    setupCRDTObserver,
    isDocumentLoading: () => isDocumentLoading(),
    getSyncingDepth: () => syncingDepth,
    incrementSyncingDepth: () => { syncingDepth++; },
    decrementSyncingDepth,
    setIsDocumentLoadingCallback: (cb) => { isDocumentLoadingCallback = cb; },
    getStore: () => store,
    handleShareRevoked,
    getMainDocId: () => docManager?.docId || null,
  });

  // Initialize node operations with callbacks
  // Note: docManager may be null (no main doc), but shared docs use getDocManagerForNode
  initNodeOperations({
    getBatchBuffer,
    getDocManager: () => docManager,
    getStore: () => store,
    getDocManagerForLinkedNode,
  });

  // Initialize batch operations with callbacks
  initBatchOperations({
    getDocManager: () => docManager,
    getStore: () => store,
    getSyncingDepth: () => syncingDepth,
    incrementSyncingDepth: () => { syncingDepth++; },
    decrementSyncingDepth,
    getDocManagerForLinkedNode,
  });

  minimalInitDone = true;
  logInfo("Minimal initialization complete");
}

/**
 * Initialize the CRDT bridge
 * @param {string} docId - Document ID to sync
 * @param {Object} options - Configuration options
 * @returns {Promise<void>}
 */
export async function init(docId, options = {}) {
  if (isEnabled) {
    logWarn("Already initialized, destroying previous instance");
    await destroy();
  }

  logInfo("Initializing for document:", docId);

  // L1 Fix (PLAN18): Reset performance metrics for new document
  resetPerformanceMetrics();

  // Reset pending remote update flag for new document
  hasPendingRemoteUpdate = false;

  // Store the isDocumentLoading callback for use in update handlers
  if (options.isDocumentLoading) {
    isDocumentLoadingCallback = options.isDocumentLoading;
  }

  // Initialize store rebuild orchestrator with callbacks
  initStoreRebuild({
    getDocManager: () => docManager,
    getSyncingDepth: () => syncingDepth,
    isDocumentLoading: () => isDocumentLoading(),
    getStore: () => store,
    getVaultStorage: () => VaultStorage,
    setLastOpenedDoc,
    getHasPendingRemoteUpdate: () => hasPendingRemoteUpdate,
    setHasPendingRemoteUpdate: (val) => { hasPendingRemoteUpdate = val; },
    getDocNotesUpdateDepth: () => docNotesUpdateDepth,
    getExecutionUpdateDepth: () => executionUpdateDepth,
    getLinkedDocTree,
    hasLinkedDoc,
    isLinkedDocAccessDenied,
    isLinkedDocGone,
    isLinkedDocOffline,
    isAccessRequestPending: (docId) => AccessRequests.isPending(docId),
    rebuildLinkedNodeOwnership,
    ensureLinkedDocsForTree,
  });

  // Initialize share lifecycle manager with callbacks
  initShareLifecycle({
    setupCRDTObserver,
    isDocumentLoading: () => isDocumentLoading(),
    getSyncingDepth: () => syncingDepth,
    incrementSyncingDepth: () => { syncingDepth++; },
    decrementSyncingDepth,
    setIsDocumentLoadingCallback: (cb) => { isDocumentLoadingCallback = cb; },
    getStore: () => store,
    handleShareRevoked,
    getMainDocId: () => docManager?.docId || null,
  });

  // Initialize node operations with callbacks
  initNodeOperations({
    getBatchBuffer,
    getDocManager: () => docManager,
    getStore: () => store,
    getDocManagerForLinkedNode,
  });

  // Initialize batch operations with callbacks
  initBatchOperations({
    getDocManager: () => docManager,
    getStore: () => store,
    getSyncingDepth: () => syncingDepth,
    incrementSyncingDepth: () => { syncingDepth++; },
    decrementSyncingDepth,
    getDocManagerForLinkedNode,
  });

  // Initialize share revocation with callbacks
  initShareRevocation({
    getDocManager: () => docManager,
    getStore: () => store,
    getSyncingDepth: () => syncingDepth,
    incrementSyncingDepth: () => { syncingDepth++; },
    decrementSyncingDepth,
    disconnectMainSync: async () => {
      if (syncManager) {
        await syncManager.disconnect();
        logInfo("Main sync disconnected (share revoked)");
      }
    },
  });

  // Mark minimal init as done (since full init includes everything minimal init does)
  minimalInitDone = true;

  // Reset corruption state from previous document (if any)
  resetCorruptionState();

  // Create Yjs document manager
  // F3.3: Removed onUpdate callback - CRDT observer handles Store rebuilds
  // This prevents duplicate rebuilds on remote updates
  docManager = new YjsDocManager(docId, {
    userId: options.userId,
    watchSize: true,
    // onUpdate: handleRemoteUpdate, // Removed - CRDT observer handles this now
    onOrphansDetected: (count) => {
      notify({ code: NOTICE.ITEMS_RECOVERED, level: "warning", message: t("sync.orphans.recovered", { count }),
        data: { mapId: docId, count }, duration: 10000 });
    },
  });

  // Initialize persistence (IndexedDB). Not for a map open by its link for
  // this page only (link-access.js): nothing of it is kept on the device.
  if (LinkAccess.pageMap(docId)) logInfo("Opened by its link for this page: not kept on this device");
  else await docManager.initPersistence();

  // Notes-serializer chunk (TipTap stack, code-split out of the boot bundle).
  // yxml-v1 docs serialize note fragments synchronously inside toTree(), so
  // the chunk must be resident before the first Store rebuild — await it.
  // For other docs just warm it in the background: the notes migrations
  // (which convert them to yxml-v1) ensure it again before running.
  //
  // A load failure (offline with a cold chunk cache) must NOT abort the doc
  // open: toTree() catches per-fragment serialize errors and falls back to
  // the cached/flat note strings, so the tree still loads — note bodies are
  // just stale until the chunk becomes fetchable.
  if (docManager.metadata.get("notesFormat") === "yxml-v1") {
    try {
      await ensureNotesSerializer();
    } catch (err) {
      logWarn("Notes serializer unavailable — note bodies may be stale:", err);
    }
  } else {
    ensureNotesSerializer().catch((err) =>
      logWarn("Notes serializer preload failed (will retry on demand):", err),
    );
  }

  // CRDT-first architecture: Set up observer to rebuild Store on ANY CRDT change
  // This ensures Store is always a projection of CRDT state
  const existingCleanup = getMainDocObserverCleanup();
  if (existingCleanup) {
    existingCleanup();
  }
  setMainDocObserverCleanup(setupCRDTObserver(docManager));

  // Bind tree UndoManager for this doc and make it active (main-doc view).
  undoManager.bind(docManager);
  undoManager.setActive(docManager.docId);

  // Create sync manager for WebSocket
  syncManager = new SyncManager({
    onStateChange: handleConnectionStateChange,
    // A map open by its link, not as a member (link-access.js): each socket
    // opens with a ticket, which says the role the link gives now.
    ...(LinkAccess.isLinkMap(docId) && { linkTicket: () => linkTicketFor(docId) }),
    // A map shared with this user as viewer: never push local state.
    isReadOnly: () => getShareRole(docId) === "viewer",
    onPermissionDenied: () => refreshDocRoleFromServer(docId),
    onChangesRefused: (info) => noteChangesRefused(docId, info),
    onPresenceChange: options.onPresenceChange,
    onError: handleSyncError,
    onRemoteUpdate: handleRemoteDocUpdate,
    onInitialSync: handleInitialSyncComplete,
    onDekRotated: (shareId) => handleDekRotated(shareId),
    onUnshared: (info) => {
      // Main sync received a `removed` message — this share was revoked.
      // Route through handleShareRevoked (same path as shared doc managers).
      const shareId = info.shareId || info.docId;
      if (shareId) {
        handleShareRevoked(shareId, info, {});
      }
    },
    onKeyOutOfSync: async () => {
      const newDek = await refreshDekFromServer(docId, syncManager, { force: true });
      if (newDek && docManager?.docId === docId) currentEncryptionKey = newDek;
      return newDek;
    },
    onSessionInvalidated: (reason) => {
      if (reason === "permission_changed") refreshDocRoleFromServer(docId);
      // Deleted by its owner, or moved to a device: say so at once if this
      // device knew it online (engine/sync/known-online.js).
      if (reason === "map_deleted") {
        void import("./known-online.js").then(async ({ isKnownOnline, stillOnline }) => {
          if (isKnownOnline(docId) && await stillOnline(docId) === false) emit(Events.MAPS_GONE_ONLINE, { docIds: [docId] });
        });
      }
      // Closed after a key rotation: fetch the new key before the reconnect
      // (sends wait for it; the handshake would also self-heal).
      if (reason === "dek_rotated") {
        refreshDekFromServer(docId, syncManager, { force: true })
          .then((newDek) => { if (newDek && docManager?.docId === docId) currentEncryptionKey = newDek; })
          .catch((err) => logWarn("Key refresh after rotation failed:", err));
      }
    },
  });

  // Seed the role guard for a shared map opened as the main document, so a
  // viewer is read-only before the first vault update event arrives.
  const openedRole = LinkAccess.isGone(docId) ? "viewer" : VaultStorage.getDoc?.(docId)?.role || LinkAccess.pageMap(docId)?.role;
  if (openedRole && openedRole !== "owner") setShareRole(docId, openedRole);

  // F5.2: Removed setupEventListeners() - no longer needed in CRDT-first architecture
  // All writes go through CRDTBridge API, CRDT observer handles Store rebuilds

  // Clear active share ID (we're using main doc now)
  setActiveShareId(null);

  isEnabled = true;
  logInfo("Initialized successfully");
}

// F5.2: Removed setupEventListeners() - CRDT-first architecture handles all sync via observer
// All writes go through CRDTBridge.addNode/updateNode/removeNode/moveNode directly

/**
 * Clean up event listeners
 */
function cleanupEventListeners() {
  eventUnsubscribers.forEach((unsub) => unsub());
  eventUnsubscribers = [];
}

/**
 * Start syncing with the server
 * @param {CryptoKey} encryptionKey - E2EE encryption key
 */
export async function startSync(encryptionKey) {
  if (!isEnabled || !docManager || !syncManager) {
    logWarn("Not initialized, cannot start sync");
    return;
  }

  // Store encryption key for reconnect after import
  currentEncryptionKey = encryptionKey;

  logInfo("Starting sync with encryption key:", encryptionKey ? "provided" : "MISSING");
  logDebug("Encryption key type:", encryptionKey ? encryptionKey.constructor.name : "N/A");

  if (encryptionKey) {
    syncManager.setEncryptionKey(encryptionKey);
    logDebug("Encryption key set on syncManager, now connecting...");
  } else {
    logWarn("No encryption key provided to startSync!");
  }

  await syncManager.connect(docManager.docId, docManager);
  logInfo("WebSocket connected");
}

/**
 * Stop syncing but keep local state
 * @returns {Promise<void>}
 */
export async function stopSync() {
  if (syncManager) {
    await syncManager.disconnect();
    logInfo("Sync stopped");
  }
}

/**
 * Sync full tree to Yjs (for initial load)
 * @param {Object} rootNode - Root node of tree
 */
export function syncFullTree(rootNode, options = {}) {
  if (!docManager) return;
  StorageHealth.assertHealthy();

  syncingDepth++;
  try {
    // options.schema: 2 for a map just created (engine/sync/map-format.js).
    docManager.fromTree(rootNode, options);
  } finally {
    decrementSyncingDepth();
  }
}

/**
 * Replace the entire CRDT tree with a new tree. Used by import flows
 * (zip / markdown / JSON) to swap the current doc's contents. The write
 * uses IMPORT_ORIGIN so the CRDT observer fires and the Store rebuilds,
 * and the UndoManager stacks are cleared so Ctrl+Z cannot resurrect
 * a pre-import state.
 *
 * Note: this path writes delete-deltas for every existing node as a
 * side effect of `fromTree`'s internal `nodes.clear()`. That matches
 * the user's intent ("replace this doc's contents") but means callers
 * that aren't actually replacing — e.g., initial-load paths — must use
 * `syncFullTree` (observer-suppressed) instead on an already-empty doc.
 *
 * @param {Object} newTree - The new tree to replace the current one
 */
export function replaceTree(newTree) {
  if (!docManager) {
    logWarn("Cannot replaceTree - not initialized");
    return;
  }
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);

  logInfo("replaceTree (import / bulk replace)");

  // Mark as a legitimate replace so fromTree doesn't log its "populated doc"
  // warning — that guard exists to catch accidental callers, not imports.
  docManager.fromTree(newTree, { isReplace: true });

  undoManager.clearFor(docManager.docId);
}

function countNodes(node) {
  if (!node) return 0;
  let count = 1;
  (node.children || []).forEach((c) => (count += countNodes(c)));
  return count;
}

// Phase 5.3: Removed dead code - handleRemoteUpdate() and applyRemoteUpdateToStore()
// These functions were replaced by the CRDT observer pattern (setupCRDTObserver)

/**
 * Flush any pending remote update that arrived during document loading.
 * Call this after setIsDocumentLoading(false) to apply deferred updates.
 * F3.3: Uses rebuildStoreFromCRDT for consistent CRDT-first architecture
 */
export function flushPendingRemoteUpdate() {
  if (hasPendingRemoteUpdate) {
    logDebug("Flushing pending remote update");
    hasPendingRemoteUpdate = false;
    rebuildStoreFromCRDT("remote");
  }
}

/**
 * Handle remote document update from WebSocket
 */
function handleRemoteDocUpdate(update) {
  logDebug("Remote doc update from WebSocket");
  // The Yjs doc will handle merging automatically
  // This callback is for notification/UI updates
  emit(Events.SYNC_REMOTE_UPDATE, { source: "websocket" });
}

/**
 * Handle connection state changes
 * @param {string} newState - New connection state
 * @param {string} oldState - Previous connection state
 */
function handleConnectionStateChange(newState, oldState) {
  logInfo(`Connection state: ${oldState} → ${newState}`);

  emit(Events.SYNC_STATE_CHANGED, {
    state: newState,
    previousState: oldState,
  });

  // NOTE: We intentionally do NOT call syncFullTree() on reconnection.
  // The CRDT sync protocol handles merging automatically via SYNC_STEP_1/2.
  // Calling syncFullTree() would overwrite CRDT data with potentially stale Store data.
  // See LAST_SESSION.md "Initiative A: Immediate Data Loss Fix" for details.
}

/**
 * Handle sync errors
 * @param {Error} error - Error object
 */
function handleSyncError(error) {
  logError("Sync error:", error);
  emit(Events.SYNC_ERROR, { error });
}

/**
 * This map's own items in a tree. The view shows each loaded linked map's
 * content under its link (store-rebuild.js): that content belongs to the
 * linked map, so a link's children are never counted or written here.
 * @param {Object} node - Root node
 * @returns {Object} copy without the children of links
 */
function ownItems(node) {
  return { ...node, children: node.linkedDocId ? [] : (node.children || []).map(ownItems) };
}

/**
 * Count nodes in a tree recursively
 * @param {Object} node - Root node
 * @returns {number} Total node count
 */
function countTreeNodes(node) {
  if (!node) return 0;
  return 1 + (node.children || []).reduce((sum, c) => sum + countTreeNodes(c), 0);
}

/**
 * Check if Store is in sync with CRDT.
 * Compares this map's own items only (`ownItems`): the view also holds the
 * content of linked maps, which once got merged into this map, sending it to
 * every member of this map and turning the link into "Open separately".
 * CRDT has more: the view is rebuilt (with the linked content). Store has
 * more: those nodes are added to the CRDT, never deleted (no data loss).
 */
function ensureStoreConsistency() {
  if (!isEnabled || !docManager) return;

  // Skip during document transitions
  if (isDocumentLoading()) return;
  // The view shows another map (a shared one): nothing to compare.
  if (getActiveShareId()) return;

  const crdtTree = docManager.toTree();
  if (!crdtTree) return;

  const storeData = store.get("data");
  if (!storeData) return;
  const crdtCount = countTreeNodes(ownItems(crdtTree));
  const storeCount = countTreeNodes(ownItems(storeData));

  if (crdtCount === storeCount) {
    logDebug("Store-CRDT consistency check passed:", crdtCount, "nodes");
    return;
  }

  if (crdtCount > storeCount) {
    // CRDT has more nodes - Store is missing nodes that should be visible
    // Safe to resync: we're adding nodes to the UI, not removing
    logWarn(`Store missing nodes! CRDT: ${crdtCount}, Store: ${storeCount}. Resyncing...`);

    // HI10 Fix: Validate CRDT tree before applying
    if (!validateTree(crdtTree, (msg) => logError("validateTree:", msg))) {
      logError("CRDT tree validation failed - NOT applying to Store");
      emit(Events.SYNC_ERROR, {
        type: "crdt_validation_failed",
        message: t("sync.corrupted.toast"),
      });
      return;
    }

    syncingDepth++;
    try {
      // The usual rebuild: it also shows the linked maps' content.
      rebuildStoreFromCRDT("consistency-check");
      logInfo("Store resynced from CRDT - added missing nodes");
      emit(Events.SYNC_REMOTE_UPDATE, { source: "consistency-check" });
    } finally {
      decrementSyncingDepth();
    }
  } else {
    // Store has more nodes than CRDT — additively merge Store nodes into CRDT.
    // We must NOT use syncFullTree (which clears CRDT first) because CRDT may
    // contain nodes that Store doesn't have — clearing would destroy them.
    logWarn(`Store has ${storeCount - crdtCount} nodes not in CRDT! CRDT: ${crdtCount}, Store: ${storeCount}. Merging Store nodes into CRDT.`);
    // Not as a viewer: the server refuses the write (and a refused change
    // is discarded with the map reloaded: decision 4 of the schema v2 plan).
    if (getShareRole(docManager.docId) === "viewer") return;
    try {
      incrementSyncingDepth();
      docManager.mergeTreeAdditive(ownItems(storeData));
      logInfo("Store nodes merged into CRDT (additive, no data loss)");
    } catch (err) {
      logWarn("Failed to merge Store nodes into CRDT:", err);
    } finally {
      decrementSyncingDepth();
    }
  }
}

/**
 * Handle initial sync completion (SYNCED state reached)
 * Fires all registered post-sync callbacks exactly once per connection.
 */
function handleInitialSyncComplete() {
  logInfo("Initial sync complete, running", postSyncCallbacks.length, "post-sync callbacks");

  // Ensure Store matches CRDT after sync completes
  // This catches cases where remote updates didn't properly rebuild Store
  ensureStoreConsistency();

  // Run and clear callbacks (they're one-time per connection)
  const callbacks = postSyncCallbacks.slice();
  postSyncCallbacks = [];

  for (const cb of callbacks) {
    try {
      const result = cb();
      // Handle async callbacks
      if (result && typeof result.catch === 'function') {
        result.catch(err => {
          logError("Post-sync callback error:", err);
        });
      }
    } catch (err) {
      logError("Post-sync callback error:", err);
    }
  }
}

/**
 * Handle a dek-rotated WebSocket broadcast.
 * Fetches the new wrapped DEK from the server, unwraps it, updates the vault,
 * updates the encryption key, and reconnects to the WebSocket.
 *
 * @param {string} shareId - The share ID (same as docId for shared docs)
 */
let _dekRotationInProgress = false;
async function handleDekRotated(shareId) {
  // A map open by its link: the new key comes through the link's key log.
  if (LinkAccess.isLinkMap(shareId)) {
    const key = await refreshLinkKey(shareId, null);
    if (key && docManager?.docId === shareId) await updateEncryptionKey(key);
    return;
  }
  // Skip if this share has been (or is being) revoked — the revocation handler
  // takes care of cleanup. Processing dek-rotated for a revoked share would cause
  // an infinite reconnect loop since WS auth rejects revoked users.
  if (isShareRevoked(shareId)) {
    logInfo("[DEK Rotation] Share already revoked, skipping:", shareId);
    return;
  }
  if (_dekRotationInProgress) {
    logInfo("[DEK Rotation] Already handling rotation, skipping duplicate for:", shareId);
    return;
  }
  _dekRotationInProgress = true;
  logInfo("[DEK Rotation] Received dek-rotated for share:", shareId);
  try {
    // Fetch new wrapped DEK for this user
    const resp = await fetch(`/api/shares/${shareId}/my-dek`, { credentials: "include" });
    if (!resp.ok) {
      logInfo("[DEK Rotation] my-dek returned", resp.status, "- skipping (possibly revoked)");
      return;
    }

    const { wrapped_dek, is_revoked } = await resp.json();

    // If this user was revoked, don't reconnect — WS auth will reject them.
    // The share_revoked notification handles recovery via user action.
    if (is_revoked) {
      logInfo("[DEK Rotation] User is revoked for share, skipping reconnect:", shareId);
      return;
    }

    // Re-check after async fetch — removed message may have arrived during the fetch
    if (isShareRevoked(shareId)) {
      logInfo("[DEK Rotation] Share revoked during DEK fetch, aborting:", shareId);
      return;
    }

    // Unwrap with own keypair
    const keypairData = VaultStorage.getIndex()?.keypair;
    if (!keypairData) {
      logWarn("[DEK Rotation] No keypair in vault — cannot handle dek-rotated");
      return;
    }

    const wrappedBytes = Keypair.fromBase64(wrapped_dek);
    const secretKey = Keypair.fromBase64(keypairData.secretKey);
    const dekBytes = await Keypair.unwrapDEKAnonymous(wrappedBytes, secretKey);
    // Zero secret key immediately after unwrap (defense-in-depth)
    secretKey.fill(0);

    const newDEK = await crypto.subtle.importKey(
      "raw", dekBytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"],
    );
    // Zero raw DEK bytes after import into CryptoKey (defense-in-depth)
    dekBytes.fill(0);

    // Update vault so future opens use the new DEK
    await VaultStorage.updateDocWrappedDEK(shareId, wrapped_dek);
    // Clear DEK cache so getDocDEK() re-unwraps
    VaultStorage.clearDEKCache(shareId);

    // Reconnect with new encryption key (triggers sync handshake → resends pending changes)
    await updateEncryptionKey(newDEK);
    logInfo("[DEK Rotation] Reconnecting with new DEK for share:", shareId);
  } catch (e) {
    logError("[DEK Rotation] Failed to handle dek-rotated:", e);
  } finally {
    _dekRotationInProgress = false;
  }
}

/**
 * Self-heal hook: AES-GCM auth-tag failure on a baseline strongly suggests
 * our local wrapped DEK is stale relative to the server's `doc_keys` row
 * (e.g., a DEK rotation committed server-side but the client crashed
 * between server-commit and `updateDocWrappedDEK`, or the local copy is
 * from a snapshot taken before the rotation). The SyncManager calls this
 * before giving up; we refetch the server's authoritative wrap, and if it
 * differs from what's in our vault we update the vault, swap the
 * SyncManager's encryption key, and signal "retry once". If the server's
 * wrap matches ours, the failure is genuinely a wrong key — return null
 * so the caller doesn't loop.
 *
 * @param {string} docId
 * @param {SyncManager} syncMgr
 * @returns {Promise<CryptoKey|null>}
 */
/**
 * Our role on a shared doc changed (the server force-closes the socket with
 * reason "permission_changed"). Pull the new role into the vault entry, which
 * updates the role guards via VAULT_INDEX_UPDATED, and into the Store for the
 * open map. Without this a demoted editor kept editing locally — edits the
 * server refuses — and a promoted viewer stayed read-only until reopening.
 */
async function refreshDocRoleFromServer(docId) {
  // A map open by its link: the role the link gives, with a new ticket.
  if (LinkAccess.isLinkMap(docId)) {
    const ticket = await LinkAccess.ticket(docId).catch((err) => { logWarn("Link role refresh failed:", docId, err); return null; });
    if (ticket) applyLinkRole(docId, ticket.role);
    return;
  }
  try {
    const resp = await fetch(`/api/shares/${docId}`, { credentials: "include" });
    if (!resp.ok) return; // revoked/removed flows handle 403/404 themselves
    const { role } = await resp.json();
    const local = VaultStorage.getDoc?.(docId);
    if (!role || !local || local.role === role || (!local.role && role === "owner")) return;
    logInfo("Role changed for", docId, ":", local.role, "->", role);
    if (role === "owner") removeShareRole(docId); else setShareRole(docId, role);
    await VaultStorage.setDocEntry({ ...local, role });
    if (store.get("currentDocId") === docId) store.setShareRole(role);
  } catch (err) {
    logWarn("Role refresh failed:", docId, err);
  }
}

/**
 * The server will not store changes this device made on a map (a write
 * refused, or a viewer's copy ahead of the server). Once the role is known,
 * announce them if this user may now only view the map and they never
 * reached the server (decision 4 of docs/plans/2026-10-02-crdt-schema-v2.md;
 * engine/sync/refused-changes.js drops them, or offers a copy).
 */
async function noteChangesRefused(docId, { offline = false } = {}) {
  await refreshDocRoleFromServer(docId);
  if (getShareRole(docId) !== "viewer" || unsyncedState(docId) !== "dirty") return;
  const mark = changeMark(docId);
  emit(Events.CHANGES_REFUSED, { docId, offline: !!offline, count: typeof mark === "number" ? mark : null });
}

/** A ticket for a socket on a map open by its link, its role applied (null: the link no longer works). */
async function linkTicketFor(docId) {
  const ticket = await LinkAccess.ticket(docId);
  if (ticket) applyLinkRole(docId, ticket.role);
  return ticket?.ticket ?? null;
}

function applyLinkRole(docId, role) {
  // Its link no longer works: nothing syncs, the copy here is read only
  // (duplicated or exported to keep working on it).
  if (LinkAccess.isGone(docId)) role = "viewer";
  if (getShareRole(docId) === role) return;
  logInfo("Link role for", docId, ":", role);
  setShareRole(docId, role);
  if (store.get("currentDocId") === docId) store.setShareRole(role);
}
on(Events.SHARE_LINK_GONE, ({ docId }) => applyLinkRole(docId, "viewer"));

/**
 * A map open by its link: its key now, the key log followed through the link
 * (changes since this device checked it). Applied to `syncMgr` when it runs
 * on another key (another page of this account may have followed the change
 * first). Null when nothing changed for this channel, or the link no longer
 * works.
 */
async function refreshLinkKey(docId, syncMgr) {
  let raw;
  try {
    raw = await LinkAccess.refreshKey(docId);
  } catch (err) {
    logWarn("Link key refresh failed:", docId, err);
    return null;
  }
  const stamp = LinkAccess.keyStamp(docId);
  if (!raw) {
    if (!syncMgr || !stamp || syncMgr._appliedLinkKey === stamp) return null;
    raw = await LinkAccess.currentKey(docId);
    if (!raw) return null;
  }
  try {
    const key = await crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
    if (syncMgr) {
      syncMgr.setEncryptionKey(key);
      syncMgr._appliedLinkKey = stamp;
    }
    return key;
  } finally {
    raw.fill(0);
  }
}

async function refreshDekFromServer(docId, syncMgr, { force = false } = {}) {
  if (LinkAccess.isLinkMap(docId)) return refreshLinkKey(docId, syncMgr);
  let resp;
  try {
    resp = await fetch(`/api/shares/${docId}/my-dek`, { credentials: "include" });
  } catch (err) {
    logWarn("DEK self-heal: network error fetching my-dek:", err);
    return null;
  }
  if (!resp.ok) {
    logWarn("DEK self-heal: my-dek returned", resp.status);
    return null;
  }
  const { wrapped_dek, is_revoked } = await resp.json().catch(() => ({}));
  if (!wrapped_dek || is_revoked) return null;

  // No-op if the server's wrap is what we already have — there's nothing
  // to recover, the genuine cause is a wrong-key failure (e.g., the
  // baseline is encrypted under a DEK that was rotated out and never
  // matched any key the user holds).
  // With `force`, the channel may still hold an older key even though the
  // vault already has the server's wrap (the owner's own rotation writes the
  // vault first, or another device updated it): re-apply the vault's key.
  const localDoc = VaultStorage.getDoc?.(docId);
  const alreadyInVault = localDoc?.wrappedDEK === wrapped_dek;
  if (alreadyInVault && !force) return null;
  // Forced, but this channel already runs on exactly this key: nothing to do.
  if (alreadyInVault && syncMgr?._appliedWrappedDek === wrapped_dek) return null;

  const keypairData = VaultStorage.getIndex()?.keypair;
  if (!keypairData) {
    logWarn("DEK self-heal: no keypair in vault");
    return null;
  }

  const wrappedBytes = Keypair.fromBase64(wrapped_dek);
  const secretKey = Keypair.fromBase64(keypairData.secretKey);
  let dekBytes;
  try {
    dekBytes = await Keypair.unwrapDEKAnonymous(wrappedBytes, secretKey);
  } catch (err) {
    logWarn("DEK self-heal: unwrap failed:", err);
    return null;
  } finally {
    secretKey.fill(0);
  }

  const newDek = await crypto.subtle.importKey(
    "raw", dekBytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"],
  );
  dekBytes.fill(0);

  if (!alreadyInVault) {
    await VaultStorage.updateDocWrappedDEK(docId, wrapped_dek);
    VaultStorage.clearDEKCache(docId);
  }
  syncMgr.setEncryptionKey(newDek);
  syncMgr._appliedWrappedDek = wrapped_dek;

  logInfo("DEK self-heal: refreshed wrapped DEK from server for", docId);
  return newDek;
}

/**
 * Register a callback to run after initial sync completes.
 * If sync is already complete (SYNCED state), the callback runs immediately.
 * Callbacks are cleared after execution or when switching documents.
 * @param {Function} callback - Function to call after sync completes
 */
export function registerPostSyncCallback(callback) {
  if (!callback || typeof callback !== 'function') return;

  // If already synced, run immediately
  if (syncManager && syncManager.getState() === ConnectionState.SYNCED) {
    logDebug("Already synced, running callback immediately");
    try {
      const result = callback();
      if (result && typeof result.catch === 'function') {
        result.catch(err => {
          logError("Post-sync callback error:", err);
        });
      }
    } catch (err) {
      logError("Post-sync callback error:", err);
    }
    return;
  }

  // Otherwise queue for when sync completes
  postSyncCallbacks.push(callback);
  logDebug("Registered post-sync callback, queue size:", postSyncCallbacks.length);
}

/**
 * Clear all pending post-sync callbacks.
 * Called when switching documents to avoid stale callbacks.
 */
export function clearPostSyncCallbacks() {
  const count = postSyncCallbacks.length;
  postSyncCallbacks = [];
  if (count > 0) {
    logDebug("Cleared", count, "pending post-sync callbacks");
  }
}

/**
 * True when the open map's local state is known to be complete: its channel
 * is synced in this session and every stored server update was read.
 * Destructive cleanups (the attachment sweep) require it — a stale or
 * partial tree would not reference attachments other members added.
 * @param {string} [docId] - must be the open map
 */
export function isDocFullySynced(docId) {
  if (!syncManager || !docManager || (docId && docId !== docManager.docId)) return false;
  return syncManager.getState() === ConnectionState.SYNCED
    && (syncManager.getUnreadablePayloads?.() || []).length === 0;
}

/**
 * Stored updates of a map that this client could not decrypt (hex SHA-256),
 * from the last sync. Non-empty while sync is paused as "unreadable".
 * @param {string} [docId] - the open map (default) or a linked map
 * @returns {string[]}
 */
export function getUnreadableUpdates(docId = docManager?.docId) {
  const channel = docId === docManager?.docId ? syncManager : linkedDocRegistry.get(docId)?.syncManager;
  return channel?.getUnreadablePayloads?.() || [];
}

/**
 * Owner or admin: set aside the stored updates of a map that nobody can
 * decrypt (S-H2), then sync again. The server moves them to quarantine
 * without deleting anything.
 * @param {string} [docId] - the open map (default) or a linked map
 * @returns {Promise<number>} number of updates set aside
 */
export async function quarantineUnreadableUpdates(docId = docManager?.docId) {
  const hashes = getUnreadableUpdates(docId);
  if (!docId || hashes.length === 0) return 0;
  const resp = await fetch(`/api/docs/${encodeURIComponent(docId)}/quarantine`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    body: JSON.stringify({ payload_sha256: hashes }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(body.error || t("sync.setAside.failed", { status: String(resp.status) }));
  const channel = docId === docManager?.docId ? syncManager : linkedDocRegistry.get(docId)?.syncManager;
  await channel?.restart?.();
  return body.moved || 0;
}

/**
 * Get current connection state
 * @returns {string} Connection state
 */
export function getConnectionState() {
  return syncManager?.getState() || ConnectionState.DISCONNECTED;
}

/**
 * Retry an affected channel after the caller has verified the advertised key.
 * Reuse the live document and offline queue so paused edits survive.
 */
export async function retryPausedMapSync(docId) {
  const channel = getCurrentDocId() === docId ? syncManager
    : linkedDocRegistry.get(docId)?.syncManager || getSharedSyncManager(docId);
  if (!channel?.docManager) return false;
  const bytes = await VaultStorage.getDocDEK(docId);
  try { channel.setEncryptionKey(await crypto.subtle.importKey('raw', bytes, 'AES-GCM', false, ['encrypt', 'decrypt'])); }
  finally { bytes.fill(0); }
  await channel.restart();
  await channel.waitForSync(10000);
  return true;
}

on(Events.SYNC_PAUSED, pause => {
  if (pause.scope !== 'map') return;
  for (const channel of new Set([
    getCurrentDocId() === pause.id ? syncManager : null,
    linkedDocRegistry.get(pause.id)?.syncManager, getSharedSyncManager(pause.id),
  ])) {
    if (channel) { channel._setState(ConnectionState.ERROR); channel._stopCompactionTimer(); }
  }
});

/** Restore sync promptly after the browser's online event. */
export function triggerReconnect() {
  if (syncManager) {
    syncManager.triggerReconnect();
  }
}

/** Whether the server refused the open map's connection: too many for this account. */
export function isRefusedTooMany() {
  return !!syncManager?.isRefusedTooMany?.();
}

/** Whether the server refused the open map's last save as busy (it is sent again shortly). */
export function isServerBusy() {
  return !!syncManager?.isServerBusy?.();
}

/**
 * Probe the connection after a tab-resume signal (visibilitychange to visible,
 * pageshow). Fires an immediate heartbeat or reconnect depending on state, so
 * a zombie socket left behind by mobile background suspension is caught fast
 * instead of waiting up to ~40s for the next scheduled heartbeat.
 */
export function probeConnection() {
  if (syncManager) {
    syncManager.probeConnection();
  }
}

/**
 * Force a full-state compaction and wait for server acknowledgement.
 * Used by the storage-broken recovery modal before reload.
 *
 * If the WebSocket is not in SYNCED state, this performs a forced restart
 * (full disconnect + reconnect) and waits up to 10 s for sync to re-establish
 * before attempting compaction. The polite triggerReconnect() is a no-op
 * for sockets stuck in CONNECTING / SYNCING / SYNCED-but-zombie states, so
 * recovery needs the harder restart() path.
 *
 * @returns {Promise<boolean>} true if compaction was acknowledged by the server
 */
export async function forceCompaction(reason = "manual") {
  if (!syncManager) return false;

  if (syncManager.getState() !== ConnectionState.SYNCED) {
    const restarted = await syncManager.restart().catch((err) => {
      logWarn(`[forceCompaction] restart failed for recovery (${reason}):`, err?.message || err);
      return false;
    });
    if (!restarted) return false;

    const synced = await syncManager.waitForSync(10000);
    if (!synced) {
      logWarn(`[forceCompaction] Could not reach SYNCED after restart (${reason})`);
      return false;
    }
  }

  return syncManager.forceCompaction(reason);
}

/**
 * Get the current Yjs document state as a Uint8Array.
 * Used by DEK rotation to re-encrypt the full state with a new key.
 * @returns {Uint8Array|null}
 */
export function getStateAsBytes() {
  if (!docManager?.doc) return null;
  return Y.encodeStateAsUpdate(docManager.doc);
}

/**
 * Update the encryption key for the current sync session and reconnect.
 * Called after receiving a dek-rotated event (via handleDekRotated).
 * Disconnects the current WebSocket and reconnects with the new key,
 * which triggers a sync handshake that resends any pending local changes.
 *
 * @param {CryptoKey} newDEK - New AES-GCM encryption key
 */
export async function updateEncryptionKey(newDEK) {
  if (!syncManager || !docManager) {
    logWarn("[DEK Rotation] updateEncryptionKey called but syncManager/docManager not ready");
    return;
  }
  currentEncryptionKey = newDEK;
  syncManager.setEncryptionKey(newDEK);
  // The offline queue is kept: it holds raw Yjs updates, encrypted with the
  // key current at send time, so they go out under the new key. Dropping
  // them lost queued deletions (the handshake re-sends only new structs).
  await syncManager.disconnect();
  await syncManager.connect(docManager.docId, docManager);
}

/**
 * Check if sync is enabled
 * @returns {boolean}
 */
export function isSyncEnabled() {
  return isEnabled;
}

/**
 * Check if currently syncing (to prevent loops)
 * @returns {boolean}
 */
export function isSyncingNow() {
  return syncingDepth > 0;
}

/**
 * Get the Yjs document manager
 * @returns {YjsDocManager|null}
 */
export function getDocManager() {
  return docManager;
}

/**
 * Get the sync manager
 * @returns {SyncManager|null}
 */
/**
 * Give the open maps' changes a moment to be sent and confirmed (signing
 * out compares this device's copies with the server right after).
 * @returns {Promise<boolean>} true when nothing is left unconfirmed
 */
export async function settlePendingWrites(timeoutMs = 3000) {
  const managers = [syncManager, ...[...linkedDocRegistry.values()].map((entry) => entry.syncManager)].filter(Boolean);
  await Promise.all(managers.map((manager) => manager._flushPendingDeltas?.().catch(() => {})));
  const settled = await Promise.all(managers.map((manager) => manager.flushPendingAcks?.(timeoutMs) ?? true));
  return settled.every(Boolean);
}

export function hasPendingWrites() {
  return !!syncManager?.hasPendingWrites() ||
    [...linkedDocRegistry.values()].some(entry => entry.syncManager?.hasPendingWrites());
}

export function getSyncManager() {
  return syncManager;
}

/** Tell the server the open map's format now (after its old copy is dropped). */
export function reportMapFormat() {
  syncManager?.reportFormat?.();
}

/**
 * Check if the CRDT document has any data
 * Used to determine if we should load from CRDT or migrate from records table
 * @returns {boolean} True if CRDT has data
 */
export function hasData() {
  return docManager?.hasData() || false;
}

/**
 * Get tree data from CRDT
 * Returns the current tree structure from the Yjs document
 * @returns {Object|null} The tree data, or null if no data
 */
export function getTreeFromCRDT() {
  if (!docManager) return null;
  return docManager.toTree();
}

/**
 * Get metadata from CRDT document
 * @param {string} key - Metadata key
 * @returns {*} The metadata value, or undefined
 */
export function getMetadata(key) {
  return docManager?.getMetadata(key);
}

/**
 * Set metadata in CRDT document
 * @param {string} key - Metadata key
 * @param {*} value - Value to set
 */
export function setMetadata(key, value) {
  if (!docManager) {
    logWarn("Cannot set metadata - not initialized");
    return;
  }
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  docManager.setMetadata(key, value);
}

/**
 * Resolve the doc manager for a docNotes op. Defaults to the main doc; pass
 * a linked doc's id to target its docNotes. Also recognises a doc mounted
 * as a shared view so docNotes tabs work whether the doc is a pure link or
 * a share-view target.
 * @param {string} [docId]
 */
function _resolveDocNotesManager(docId) {
  if (!docId || docId === docManager?.docId) return docManager;
  // A linked map's notes are served only once the server confirmed access
  // (or from its offline copy): a revoked map's cache must not reappear.
  const entry = linkedDocRegistry.get(docId);
  if (entry?.docManager && (entry.verified || _hasOfflineCopy(docId, entry))) return entry.docManager;
  return getSharedDocManager(docId) || null;
}

/**
 * True when doc notes of `docId` must not be changed here: a linked or
 * shared map the user only views, or a linked map shown from its offline
 * copy. The open map follows the Store's read-only state.
 * @param {string|null} [docId]
 */
export function isDocNotesReadOnly(docId) {
  if (!docId || docId === docManager?.docId) return !!store?.isReadOnly?.();
  return getShareRole(docId) === "viewer" || isLinkedOffline(docId) || isDocQuotaBlocked(docId) || isMapFull(docId);
}

function _blockDocNoteWrite(docId) {
  if (!isDocNotesReadOnly(docId)) return false;
  logWarn("Doc note write blocked: read-only map", docId);
  emit(Events.EDIT_BLOCKED, { action: "update", reason: t("sync.edit.notesReadOnly") });
  return true;
}

/**
 * Get document-level notes from CRDT (Y.Array)
 * @param {string} [docId] - Defaults to main doc. Pass a linked doc id to read
 *   that linked doc's docNotes (used by the notes-tab UI).
 * @returns {Array} Array of note objects, or empty array if none
 */
export function getDocNotes(docId) {
  return _resolveDocNotesManager(docId)?.getDocNotes() || [];
}

/** Read-only backlinks across the main map and its currently loaded links. */
export function getRelatedDocNotes(task) {
  if (!docManager || !task) return [];
  const result = [];
  for (const doc of listDocsWithNotes()) {
    const dm = _resolveDocNotesManager(doc.docId);
    if (!dm) continue;
    const matchesReference = (raw, execution) => {
      const ref = typeof raw === 'string' ? { nodeId: raw } : raw;
      if (!ref?.nodeId) return false;
      let location = { docId: ref.linkedDocId || doc.docId, nodeId: ref.nodeId };
      // New execution entries in the main map contain composited node IDs.
      // Legacy references already specify their owner's canonical identity.
      if (execution && !ref.linkedDocId) {
        if (doc.isMain) {
          location = getNodeLocation(ref.nodeId);
          location.docId ||= doc.docId;
        } else {
          const node = dm.getNode(ref.nodeId);
          const target = node?.linkedDocId && _resolveDocNotesManager(node.linkedDocId);
          if (target) location = { docId: node.linkedDocId, nodeId: target.toTree()?.id };
        }
      }
      return location.docId === task.docId && location.nodeId === task.nodeId;
    };
    const reasons = relatedNoteReasons({ notes: dm.getDocNotes(),
      execution: ExecutionMap.listExecutionItems(dm, { includeRemoved: true }),
      routines: Routines.getRoutineState(dm), matchesReference });
    for (const [noteId, reason] of reasons) result.push({ docId: doc.isMain ? null : doc.docId, noteId, reason });
  }
  return result;
}

/**
 * Set document-level notes in CRDT (bulk replacement - use for imports only)
 * For normal operations, use addDocNote/removeDocNote/updateDocNote instead.
 * @param {Array} notes - Array of note objects
 */
export function setDocNotes(notes) {
  if (!docManager) {
    logWarn("Cannot set docNotes - not initialized");
    return;
  }
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  docManager.setDocNotes(notes);
}

/**
 * Add a single document note (preferred for normal operations)
 * CR6 Fix: Uses depth counter to skip Store rebuild - docNotes don't affect tree structure
 * @param {Object} note - Note object with id, title, content, created, updated, refnotes
 */
export function addDocNote(note, docId) {
  const dm = _resolveDocNotesManager(docId);
  if (!dm) {
    logWarn("Cannot add docNote - no doc manager for", docId);
    return;
  }
  if (dm !== docManager && _blockDocNoteWrite(dm.docId)) return;
  StorageHealth.assertHealthy();
  assertDocQuota(dm.docId);
  logDebug("addDocNote - docManager.docId:", dm.docId);
  docNotesUpdateDepth++;
  try {
    dm.addDocNote(note);
    // Fresh local documents can create their first note before ever reaching
    // the loader's post-open reconciliation. Enable its list immediately.
    if (dm === docManager) ExecutionMap.migrateRefnotesToExecution(dm);
  } finally {
    docNotesUpdateDepth--;
  }
}

/**
 * Remove a document note by ID
 * CR6 Fix: Uses depth counter to skip Store rebuild - docNotes don't affect tree structure
 * @param {string} noteId - ID of the note to remove
 * @returns {boolean} True if note was found and removed
 */
export function removeDocNote(noteId, docId) {
  const dm = _resolveDocNotesManager(docId);
  if (!dm) {
    logWarn("Cannot remove docNote - no doc manager for", docId);
    return false;
  }
  if (dm !== docManager && _blockDocNoteWrite(dm.docId)) return false;
  StorageHealth.assertHealthy();
  assertDocQuota(dm.docId);
  docNotesUpdateDepth++;
  try {
    return dm.removeDocNote(noteId);
  } finally {
    docNotesUpdateDepth--;
  }
}

/**
 * Update a document note by ID
 * CR6 Fix: Uses depth counter to skip Store rebuild - docNotes don't affect tree structure
 * @param {string} noteId - ID of the note to update
 * @param {Object} updates - Properties to update (title, content, updated, refnotes)
 * @returns {boolean} True if note was found and updated
 */
export function updateDocNote(noteId, updates, docId) {
  const dm = _resolveDocNotesManager(docId);
  if (!dm) return false;
  if (dm !== docManager && _blockDocNoteWrite(dm.docId)) return false;
  StorageHealth.assertHealthy();
  assertDocQuota(dm.docId);
  docNotesUpdateDepth++;
  try {
    return dm.updateDocNote(noteId, updates);
  } finally {
    docNotesUpdateDepth--;
  }
}

// ============================================
// Execution map (Today lists) — see engine/sync/execution-map.js
// ============================================

function withExecutionWrite(fn) {
  if (!docManager) {
    logWarn("Execution map: not initialized");
    return null;
  }
  if (store.isReadOnly()) return null;
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  executionUpdateDepth++;
  try {
    return fn(docManager);
  } finally {
    executionUpdateDepth--;
  }
}

export function getRoutineEvents() { return Routines.readRoutineEvents(docManager); }
export function getRoutinesState() { return Routines.getRoutineState(docManager); }
export function setRoutineEvents(events) {
  if(!docManager||store.isReadOnly())throw new Error(t('sync.routine.importReadOnly'));
  return withExecutionWrite(dm => Routines.importRoutineEvents(dm, events));
}
export function changeRoutine(action, data = {}) {
  if (!docManager || store.isReadOnly()) throw new Error(t('sync.routine.readOnly'));
  if(docManager.docId!==store.get('currentDocId'))throw new Error(t('sync.routine.mapOpening'));
  return withExecutionWrite(dm => {
    const state = getRoutinesState();
    const r = state.routines.find(r => r.nodeId === data.nodeId);
    const snapshotStep=s=>{
      const node=dm.getNode(s.nodeId);if(!node)return s;
      const notes=dm.metadata.get('notesFormat')==='yxml-v1'?dm.serializeNodeNotes(s.nodeId):(typeof node.notes==='string'?node.notes:'');
      return {...s,title:node.name||s.title,notes};
    };
    if (action === 'save') return Routines.saveRoutine(dm, data.nodeId, {...data.value,steps:(data.value.steps||[]).map(snapshotStep)}, data.parents || []);
    if (action === 'start' || action === 'complete') {
      if (!r || !dm.hasNode(r.nodeId)) throw new Error(t('sync.routine.unavailable'));
      const occurrence = data.occurrence || Routines.nextOccurrence(r, state);
      let definition=r;
      if(data.occurrence){
        let identity;try{identity=JSON.parse(occurrence.id);}catch{throw new Error('Invalid occurrence.');}
        const version=r.versions.filter(v=>v.scheduleId===identity[1]).at(-1);
        if(!version||identity[0]!==r.nodeId)throw new Error(t('sync.routine.otherOccurrence'));
        definition={...version,paused:r.paused,archived:r.archived,conflict:r.conflict};
      }
      if(data.noteId && !getDocNotes().some(note=>note.id===data.noteId))throw new Error(t('sync.routine.noteUnavailable'));
      if(action==='complete' && (!occurrence?.id || definition.paused || definition.archived || definition.conflict))throw new Error(t('sync.routine.resolveFirst'));
      const steps = (definition.steps.length ? definition.steps : [{id:'task',nodeId:r.nodeId,title:r.title,minutes:0,target:''}]).map(snapshotStep);
      const currentDefinition = {...definition,title:store.find(r.nodeId)?.name||definition.title};
      if(action==='complete')return setOccurrenceDone(dm, currentDefinition, occurrence, steps, data.noteId, !!data.done, data.runIds);
      return Routines.startRoutine(dm,currentDefinition,occurrence,steps,data.noteId);
    }
    if (action === 'link') {
      if (!r || !getDocNotes().some(note=>note.id===data.noteId) || !data.occurrence?.id) throw new Error(t('sync.routine.selectNote'));
      let event;
      dm.doc.transact(() => {
        if (!data.removed) {
          markNodeInProgress(r.nodeId);
          if(!state.links.some(l=>l.noteId===data.noteId&&l.occurrenceId===data.occurrence.id)) {
            const order = firstTaskListOrder(data.noteId);
            Routines.appendRoutineEvent(dm,{kind:'position',nodeId:r.nodeId,noteId:data.noteId,occurrenceId:data.occurrence.id,order});
          }
        }
        event = Routines.appendRoutineEvent(dm,{kind:'link',nodeId:r.nodeId,noteId:data.noteId,
          occurrenceId:data.occurrence.id,day:data.occurrence.day,title:r.title,removed:!!data.removed});
      }, USER_ORIGIN);
      return event;
    }
    if(action==='anchor') {
      if(!r||!data.occurrence?.conflict||!validDay(data.day))throw new Error(t('sync.routine.chooseCompletionDate'));
      const parents=(state.anchors.get(data.occurrence.id)||[]).map(e=>e.id);
      if(JSON.stringify(parents.sort())!==JSON.stringify([...(data.parents||[])].sort()))throw new Error(t('sync.routine.completionChanged'));
      return Routines.appendRoutineEvent(dm,{kind:'anchor',nodeId:r.nodeId,occurrenceId:data.occurrence.id,day:data.day,parents});
    }
    if (action === 'exception') {
      if (!r || !data.occurrence?.id || !validDay(data.day)) throw new Error(t('sync.routine.chooseNextDate'));
      return Routines.appendRoutineEvent(dm,{kind:'exception',nodeId:r.nodeId,occurrenceId:data.occurrence.id,
        day:data.day,scheduledDay:data.occurrence.day,nextDay:data.day,skipped:!!data.skipped});
    }
    const run=state.runs.find(run=>run.runId===data.runId);
    if(!run)throw new Error(t('sync.routine.sessionUnavailable'));
    if(action==='result') {
      if(run.state!=='active')throw new Error(t('sync.routine.sessionFinished'));
      if(data.expectedTimer) {
        const result=run.results[data.stepId]||{};
        const current={seconds:result.seconds||0,runningAt:result.runningAt||null,timerOwner:result.timerOwner||''};
        if(Object.keys(current).some(key=>current[key]!==data.expectedTimer[key]))throw new Error(t('sync.routine.timerChanged'));
      }
      if(!run.steps.some(s=>s.id===data.stepId))throw new Error('Unknown step.');
      const values=data.values||{};
      if(values.seconds!==undefined&&(!Number.isFinite(values.seconds)||values.seconds<0||values.seconds>MAX_ROUTINE_SECONDS))throw new Error(t('sync.routine.durationRange'));
      if(values.done!==undefined&&typeof values.done!=='boolean')throw new Error('Invalid completion value.');
      if(values.result!==undefined&&typeof values.result!=='string')throw new Error('Invalid session note.');
      if(values.runningAt!==undefined&&values.runningAt!==null&&(!Number.isFinite(Date.parse(values.runningAt))||typeof values.runningAt!=='string'))throw new Error('Invalid timer value.');
      const safe={};
      if(values.timerOwner!==undefined&&typeof values.timerOwner!=='string')throw new Error('Invalid timer owner.');
      for(const key of ['seconds','done','result','runningAt','timerOwner'])if(values[key]!==undefined)safe[key]=values[key];
      return Routines.appendRoutineEvent(dm,{kind:'result',runId:run.runId,stepId:data.stepId,values:safe});
    }
    if(action==='finish') {
      if(!['done','partial','active'].includes(data.state))throw new Error('Unknown session status.');
      const definition=state.routines.find(r=>r.nodeId===run.nodeId);
      const day=data.day||dayKey(new Date(),run.zone||definition?.schedule?.zone);
      if(!validDay(day))throw new Error(t('sync.routine.validCompletionDate'));
      return Routines.appendRoutineEvent(dm,{kind:'finish',runId:run.runId,state:data.state,day});
    }
    throw new Error('Unknown routine action.');
  });
}

/** Items of one period (or all), sorted; removed entries excluded unless asked. */
export function getExecutionItems(options = {}) {
  if (!docManager) return [];
  const items = ExecutionMap.listExecutionItems(docManager, options);
  // Export/history readers receive the original records. Display readers get
  // a derived return date for grouping; postponement never hides a reminder.
  if (options.includeRemoved) return items;
  const pending = TaskPostponements.pendingPostponements(docManager);
  const today = ExecutionMap.localDateString();
  return items.map(item => ({ ...item, postponedUntil:
    TaskPostponements.isPostponed(item, pending, today) ? pending.get(item.nodeId).until : null }));
}

/** Mixed display only; export and execution statistics retain their existing records. */
export function getTaskListItems(noteId) {
  const current = docManager && currentExecutionNote(docManager)?.id === noteId;
  const routines = routineListItems(getRoutinesState(), noteId, current)
    .filter(item => docManager?.hasNode(item.nodeId) || item.runIds.length);
  return mergeTaskList(getExecutionItems({noteId}), routines);
}
function firstTaskListOrder(noteId) {
  return getTaskListItems(noteId).reduce((min, item) => Math.min(min, item.order), 0) - 1;
}
export function setTaskListOrder(noteId, orderedIds) {
  return withExecutionWrite(dm => {
    const items = getTaskListItems(noteId), byId = new Map(items.map(item => [item.executionId, item]));
    const ids = [...new Set([...orderedIds.filter(id => byId.has(id)), ...items.map(item => item.executionId)])];
    dm.doc.transact(() => {
      ids.forEach((id, order) => {
        const item = byId.get(id);
        if(item.order === order)return;
        if(item.routine)Routines.appendRoutineEvent(dm,{kind:'position',nodeId:item.nodeId,noteId,occurrenceId:item.occurrence.id,order});
        else ExecutionMap.sendToExecution(dm,noteId,item.nodeId,{order});
      });
    }, USER_ORIGIN);
  });
}

export function getExecutionItem(noteId, nodeId) {
  if (!docManager) return null;
  return ExecutionMap.getExecutionItem(docManager, noteId, nodeId);
}

function currentExecutionNote(dm) {
  return dm.getDocNotes().filter(note => note?.id && Number.isFinite(Date.parse(note.created)))
    .sort((a, b) => Date.parse(a.created) - Date.parse(b.created) || String(a.id).localeCompare(String(b.id))).at(-1);
}
export function getTaskPostponement(nodeId) {
  return TaskPostponements.pendingPostponements(docManager).get(nodeId) || null;
}
export function getTaskPostponementEvents() { return TaskPostponements.readPostponementEvents(docManager); }
export function setTaskPostponementEvents(events) {
  return withExecutionWrite(dm => TaskPostponements.importPostponementEvents(dm, events));
}
export function canPostponeTask(nodeId) {
  return !!docManager?.hasNode(nodeId) && docManager.docId === store.get("currentDocId") &&
    nodeId !== store.getState()?.data?.id &&
    !store.isReadOnly() && !nodeIsDone(store.find(nodeId)) &&
    !Routines.getRoutineState(docManager).routines.some(routine => routine.nodeId === nodeId && !routine.archived);
}
export function postponeTask(nodeId, until) {
  if (!canPostponeTask(nodeId)) throw new Error(t('sync.postpone.notAllowed'));
  if (!validDay(until) || until <= ExecutionMap.localDateString()) throw new Error(t('sync.postpone.afterToday'));
  return withExecutionWrite(dm => {
    ExecutionMap.migrateRefnotesToExecution(dm);
    return TaskPostponements.writePostponement(dm, nodeId, until, generateNodeId());
  });
}
export function bringTaskBack(nodeId) {
  if (!canPostponeTask(nodeId)) throw new Error(t('sync.postpone.cannotChange'));
  const note = currentExecutionNote(docManager);
  if (!note) throw new Error(t('sync.postpone.noNote'));
  return sendToExecution(note.id, nodeId);
}
/** Keep pending reminders in the latest note and return them when due.
 * Adding a reminder preserves task status, pins and earlier work marks.
 * Only an actual return records a receipt or starts execution.
 */
export function returnDueTasks(today = ExecutionMap.localDateString()) {
  if (!docManager || store.isReadOnly() || !isExecutionEnabled()) return 0;
  const note = currentExecutionNote(docManager);
  if (!note) return 0;
  const pending = [...TaskPostponements.pendingPostponements(docManager).values()]
    .filter(event => canPostponeTask(event.nodeId));
  const actions = pending.filter(event => {
    const entry = ExecutionMap.getExecutionItem(docManager, note.id, event.nodeId);
    return event.until <= today || !entry || entry.removedAt;
  });
  if (!actions.length) return 0;
  return withExecutionWrite(dm => {
    const notes = new Map(dm.getDocNotes().map(n => [n.id, Date.parse(n.created) || 0]));
    const previous = new Map(ExecutionMap.listExecutionItems(dm)
      .sort((a, b) => (notes.get(a.noteId) || 0) - (notes.get(b.noteId) || 0)
        || String(a.noteId).localeCompare(String(b.noteId)))
      .map(entry => [entry.nodeId, entry]));
    let returned = 0;
    dm.doc.transact(() => {
      for (const event of actions) {
        const entry = ExecutionMap.getExecutionItem(dm, note.id, event.nodeId);
        ExecutionMap.sendToExecution(dm, note.id, event.nodeId,
          entry ? {} : { pinned: previous.get(event.nodeId)?.pinned || false });
        if (event.until <= today) {
          markNodeInProgress(event.nodeId);
          TaskPostponements.recordTaskReturn(dm, event, note.id);
          returned++;
        }
      }
    }, USER_ORIGIN);
    return returned;
  });
}

/** Shared by task execution entries, routine links and legacy note references. */
export function markNodeInProgress(nodeId) {
  if (!docManager || store.isReadOnly() || isNodeReadOnly(nodeId)) return false;
  const {docManager: owner, translatedId} = getDocManagerForLinkedNode(nodeId);
  // References may be listed even when their source document is read-only.
  if (!owner || !owner.hasNode(translatedId)) return false;
  StorageHealth.assertHealthy();
  assertDocQuota(owner.docId);
  owner.doc.transact(() => {
    owner.initDefaultTags(generateNodeId);
    const node = owner.getNode(translatedId);
    const tags = inProgressTags(owner.getTagDefinitions(), node.tags);
    const legacyStatusChanged = node.status && node.status !== 'in-progress';
    if (legacyStatusChanged || JSON.stringify(tags) !== JSON.stringify(node.tags)) {
      owner.updateNode(translatedId, {tags, ...(node.status ? {status: 'in-progress'} : {})}, USER_ORIGIN);
    }
  }, USER_ORIGIN);
  return true;
}

export function sendToExecution(noteId, nodeId, options = {}) {
  if (!noteId || !nodeId) return null;
  return withExecutionWrite(dm => {
    let entry;
    dm.doc.transact(() => {
      markNodeInProgress(nodeId);
      if (!options.keepPostponement && getTaskPostponement(nodeId)) TaskPostponements.writePostponement(dm, nodeId, null, generateNodeId());
      const existing = ExecutionMap.getExecutionItem(dm, noteId, nodeId);
      const order = options.order ?? ((!existing || existing.removedAt) ? firstTaskListOrder(noteId) : null);
      entry = ExecutionMap.sendToExecution(dm, noteId, nodeId, {...options, order});
    }, USER_ORIGIN);
    return entry;
  });
}

export function removeFromExecution(noteId, nodeId) {
  return withExecutionWrite(dm => {
    let removed = false;
    dm.doc.transact(() => {
      removed = ExecutionMap.removeFromExecution(dm, noteId, nodeId);
      if (removed && noteId === currentExecutionNote(dm)?.id && getTaskPostponement(nodeId)) {
        TaskPostponements.writePostponement(dm, nodeId, null, generateNodeId());
      }
    }, USER_ORIGIN);
    return removed;
  });
}

export function tickWorked(noteId, nodeId, date, on = true) {
  return withExecutionWrite((dm) => ExecutionMap.tickWorked(dm, noteId, nodeId, date, on));
}

export function setExecutionWorked(noteId, nodeId, on, observedDates) {
  return withExecutionWrite((dm) => {
    // The tick counts for the note's day, not the moment of the click.
    const note = dm.getDocNotes().find((n) => n.id === noteId);
    const day = noteDay(note) || ExecutionMap.localDateString();
    return ExecutionMap.setExecutionWorked(dm, noteId, nodeId, on, observedDates, day);
  });
}

export function setExecutionPinned(noteId, nodeId, pinned) {
  return withExecutionWrite((dm) => ExecutionMap.setPinned(dm, noteId, nodeId, pinned));
}

export function setExecutionOrder(noteId, orderedNodeIds) {
  return withExecutionWrite((dm) => ExecutionMap.setExecutionOrder(dm, noteId, orderedNodeIds));
}

/** Previous period's unfinished items with their carried-without-work count. */
export function getCarryCandidates(currentNoteId, isDone) {
  if (!docManager) return [];
  const notes = [...docManager.getDocNotes()].sort((a, b) =>
    String(a.created || "").localeCompare(String(b.created || "")));
  const pending = TaskPostponements.pendingPostponements(docManager);
  return ExecutionMap.carryCandidates(docManager, notes, currentNoteId, isDone)
    .filter(item => !TaskPostponements.isPostponed(item, pending, ExecutionMap.localDateString()));
}

// ---- derived statistics (execution-stats.js), cached until anything relevant changes
let executionStatsCache = null;
let executionStatsListening = false;

function invalidateExecutionStats() {
  executionStatsCache = null;
}

function ensureExecutionStatsListeners() {
  if (executionStatsListening) return;
  executionStatsListening = true;
  // Ticks / sends, tree edits (tags, estimates, moves) and new doc notes (new periods) all change the stats.
  for (const ev of [Events.EXECUTION_CHANGED, Events.DATA_CHANGED, Events.DATA_LOADED,
                    Events.CRDT_PROJECTION_UPDATED, Events.DOC_NOTES_CHANGED]) {
    on(ev, invalidateExecutionStats);
  }
}

/** Top-level branch (child of root) containing a node, via the Store tree. */
function topBranchIdOf(nodeId) {
  const rootId = store.getState?.()?.data?.id;
  let cur = nodeId;
  for (let guard = 0; guard < 500; guard++) {
    const parent = store.findParent?.(cur);
    if (!parent) return cur === rootId ? null : cur;
    if (parent.id === rootId) return cur;
    cur = parent.id;
  }
  return null;
}

/** ISO time the Done tag was applied, or null. */
export function nodeDoneAt(node) {
  if (!node?.tags?.length) return null;
  const doneId = getMandatoryTagId('Done', node.id);
  const t = node.tags.find((e) => typeof e !== 'string' && e.tagId === doneId && e.setAt);
  return t ? t.setAt : null;
}

/**
 * Derived execution statistics for the current document — periods, per-node
 * execution clock, load, allocation, weekly ticks, starvation, and the set of
 * nodes in execution. Cached; recomputed after any relevant change.
 * @param {{ today?: string, starvationPeriods?: number }} [options]
 */
export function getExecutionStats(options = {}) {
  if (!docManager) return computeExecutionStats();
  ensureExecutionStatsListeners();
  const key = JSON.stringify({ today: ExecutionMap.localDateString(), ...options });
  if (executionStatsCache && executionStatsCache.key === key) return executionStatsCache.value;
  const currentNoteId = currentExecutionNote(docManager)?.id;
  const pending = TaskPostponements.pendingPostponements(docManager);
  const value = computeExecutionStats({
    entries: ExecutionMap.listExecutionItems(docManager, { includeRemoved: true }).map(item => ({ ...item,
      postponed: item.noteId === currentNoteId && TaskPostponements.isPostponed(item,
        pending, options.today || ExecutionMap.localDateString()),
    })),
    docNotes: docManager.getDocNotes(),
    getNode: (id) => store.find?.(id) || null,
    isDone: nodeIsDone,
    doneAt: nodeDoneAt,
    getBranchId: topBranchIdOf,
    ...options,
  });
  executionStatsCache = { key, value };
  return value;
}

/** True once the document's execution map is in use (refnotes migrated or entries written). */
export function isExecutionEnabled() {
  if (!docManager) return false;
  return docManager.metadata.get(ExecutionMap.EXECUTION_FORMAT_KEY) === ExecutionMap.EXECUTION_FORMAT_V1;
}

// The "Unsorted" branch is created with its name in the app's language
// (sync.unsorted.name) and found by name. It is found under its name in
// every language, so devices and members using another language share one
// branch. Keep this list in step with locales/sync/*.js: add a name when a
// translation changes, never remove one (existing maps keep the old name).
const UNSORTED_BRANCH_NAMES = [
  "Unsorted", "À trier", "Sin clasificar", "Unsortiert", "Da smistare",
  "Não classificados", "Ongesorteerd", "Nieposortowane", "未分類", "未分类",
];
const isUnsortedName = (node) => {
  const name = String(node?.name || "").trim().toLowerCase();
  if (!name) return false;
  return name === t("sync.unsorted.name").toLowerCase() ||
    UNSORTED_BRANCH_NAMES.some((known) => known.toLowerCase() === name);
};

/**
 * Move a node (node-operations.js). A node leaving the root-level "Unsorted"
 * branch then takes the colour of its new place, with its subtree, as a node
 * created there would: its new parent's colour, or at the root a colour its
 * new siblings don't use. The move itself is unchanged (not batched: batch
 * mode routes links to other maps differently); the recolouring follows as
 * its own step, so undo reverts the colour first, then the move.
 * @returns {boolean} true if the move was applied
 */
export function moveNode(nodeId, newParentId, index = -1) {
  return moveNodeRecolored(nodeId, newParentId, index, []);
}

// moveNode, with the colours already given to other nodes moved to the root
// in the same batch (the Store only shows them once the batch is applied).
function moveNodeRecolored(nodeId, newParentId, index, usedColors) {
  const root = store.getState?.()?.data;
  const oldParent = store.findParent(nodeId);
  const leavesUnsorted = !!root && oldParent?.id !== newParentId && isUnsortedName(oldParent) &&
    (root.children || []).some((c) => c.id === oldParent.id);
  const color = !leavesUnsorted ? null : newParentId === root.id
    ? newItemColor([...(root.children || []).filter((c) => c.id !== nodeId).map((c) => c.color), ...usedColors])
    : store.find(newParentId)?.color;
  if (color && newParentId === root?.id) usedColors.push(color);

  const moved = moveNodeOp(nodeId, newParentId, index);
  if (!moved || !color) return moved;
  const recolored = [];
  (function collect(node) {
    if (!node) return;
    if (node.color !== color) recolored.push(node.id);
    (node.children || []).forEach(collect);
  })(store.find(nodeId));
  if (recolored.length) {
    beginBatch();
    try {
      for (const id of recolored) updateNode(id, { color });
    } finally {
      endBatch();
    }
  }
  return moved;
}

/**
 * Move several nodes under one parent (multi-selection). A selected node
 * inside another selected one moves with it; the others keep their order in
 * the map and land together at `index` (or at the end). Refused when the
 * target is inside a moved node. One batch, so a single undo reverts it —
 * except links to other maps, which batch mode routes differently: those
 * move one by one after it. A move the guards refuse (another map, read-only)
 * leaves that node where it was; `movedIds` lists the nodes that really moved
 * out of the `attempted` ones.
 * @returns {{moved: number, movedIds: string[], attempted: number, refused?: "inside"}}
 */
export function moveNodes(nodeIds, newParentId, index = -1) {
  const root = store.getState?.()?.data;
  const target = store.find(newParentId);
  if (!root || !target || !nodeIds?.length) return { moved: 0, movedIds: [], attempted: 0 };
  const selected = new Set(nodeIds);
  const ordered = [];
  (function walk(node, underSelected) {
    const isSelected = selected.has(node.id);
    if (isSelected && !underSelected) ordered.push(node.id);
    (node.children || []).forEach((child) => walk(child, underSelected || isSelected));
  })(root, false);
  const moving = new Set(ordered);
  for (let n = target; n; n = store.findParent(n.id)) {
    if (moving.has(n.id)) return { moved: 0, movedIds: [], attempted: ordered.length, refused: "inside" };
  }

  // Insert before the first sibling at `index` that stays; replay the moves
  // on a copy of the child list to get each move's index (remove, then insert).
  const siblings = (target.children || []).map((c) => c.id);
  const anchorId = index >= 0 ? siblings.slice(index).find((id) => !moving.has(id)) ?? null : null;
  const indexFor = (id) => {
    const at = siblings.indexOf(id);
    if (at >= 0) siblings.splice(at, 1);
    const pos = anchorId ? siblings.indexOf(anchorId) : -1;
    if (pos >= 0) siblings.splice(pos, 0, id);
    else siblings.push(id);
    return pos;
  };

  // A move refused up front leaves the simulated list as it was.
  const usedColors = [];
  const place = (id) => {
    const before = siblings.slice();
    if (!moveNodeRecolored(id, newParentId, indexFor(id), usedColors)) siblings.splice(0, siblings.length, ...before);
  };
  const isLink = (id) => !!getDocManagerForLinkedNode(id)?.isStub;
  const direct = ordered.filter((id) => !isLink(id));
  if (direct.length) {
    beginBatch();
    try {
      direct.forEach(place);
    } finally {
      endBatch();
    }
  }
  ordered.filter(isLink).forEach(place);
  // Batched moves are checked again when applied: count what really moved.
  const movedIds = ordered.filter((id) => store.findParent(id)?.id === newParentId);
  return { moved: movedIds.length, movedIds, attempted: ordered.length };
}

/**
 * The root-level "Unsorted" branch that free lines typed into Today go to
 * (design spec §3.1, §6). Found by name (case-insensitive) or created.
 * @returns {string|null} node id
 */
export function ensureUnsortedBranch() {
  if (store.isReadOnly()) return null;
  const root = store.getState?.()?.data;
  if (!root) return null;
  const existing = (root.children || []).find(isUnsortedName);
  if (existing) return existing.id;
  const id = generateNodeId();
  addNode(root.id, { id, name: t("sync.unsorted.name"), color: "#6b7280", children: [] });
  return id;
}

/** Bulk import of execution entries (per-map JSON import); union, never overwrites. */
export function setExecutionItems(entries) {
  return withExecutionWrite((dm) => ExecutionMap.setExecutionItems(dm, entries));
}

/**
 * Migrate legacy doc-note refnotes into execution entries (additive,
 * idempotent). Called post-sync like the other migrations.
 */
export function migrateRefnotesToExecution() {
  if (!docManager || store.isReadOnly()) return { migrated: false, count: 0 };
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  executionUpdateDepth++;
  try {
    const result = ExecutionMap.migrateRefnotesToExecution(docManager);
    if (result.migrated) logInfo(`Execution map: migrated ${result.count} refnotes into execution entries`);
    return result;
  } finally {
    executionUpdateDepth--;
  }
}

/**
 * List {docId, title} for the main doc + every active linked doc, for the
 * notes overlay/view tab bar. The linked doc titles are pulled from their
 * respective stubs in the current main tree.
 * @returns {Array<{docId: string, title: string, isMain: boolean}>}
 */
export function listDocsWithNotes() {
  const result = [];
  const tree = store?.getState?.()?.data;
  if (docManager) {
    result.push({
      docId: docManager.docId,
      title: tree?.name || t("sync.notes.mainDocument"),
      isMain: true,
    });
  }
  // Iterate stubs in the composited tree so we pick up every linked doc
  // surfaced in main — including those backed by a shared-doc manager
  // (not tracked in linkedDocRegistry) and those still pending sync
  // (not yet in linkedDocRegistry either).
  const cachedTitles = new Map();
  const linkedDocIds = new Set();
  (function walk(node) {
    if (!node) return;
    // Only links whose content is shown here: a nested, duplicate,
    // conflicting, no-access or still-loading link has no notes to offer.
    const shown = node._linkedState === "loaded" || node._linkedState === "offline";
    if (node.linkedDocId && shown && !node._nestedReference && !node._duplicate && !node._idConflict) {
      linkedDocIds.add(node.linkedDocId);
      if (node.linkedDocTitle) cachedTitles.set(node.linkedDocId, node.linkedDocTitle);
    }
    if (node.children) node.children.forEach(walk);
  })(tree);
  for (const docId of linkedDocIds) {
    if (!_resolveDocNotesManager(docId)) continue;
    // Prefer the live root name from whichever manager holds the doc;
    // fall back to the stub's cached title.
    const mgr = _resolveDocManagerById(docId);
    const liveName = mgr?.toTree()?.name;
    result.push({
      docId,
      title: liveName || cachedTitles.get(docId) || t("sync.linked.defaultTitle"),
      isMain: false,
    });
  }
  return result;
}

/**
 * Whether this device may write a map's migrations: not over the quota, and
 * not as a viewer — the server refuses a viewer's writes, and a refused
 * change is discarded with the map reloaded from the server (decision 4 of
 * the schema v2 plan), which would run the migration again.
 */
function canMigrate(docId) {
  return !isDocQuotaBlocked(docId) && getShareRole(docId) !== "viewer";
}

/**
 * Migrate notes from old storage locations to Y.Array
 * Should be called after CRDT sync is established.
 */
export function migrateDocNotes() {
  if (!docManager) {
    logWarn("Cannot migrate docNotes - not initialized");
    return;
  }
  StorageHealth.assertHealthy();
  if (!canMigrate(docManager.docId)) return;
  docManager.migrateDocNotes();
  // Rename tags → refnotes on doc notes (frees 'tags' for node tagging)
  docManager.migrateDocNoteRefnotes();
  // Initialize default tag definitions if none exist
  docManager.initDefaultTags(generateNodeId);
  // Migrate node status to tags
  docManager.migrateStatusToTags();
  // Initialize default kanban columns
  initKanbanColumns();
}

/**
 * Migrate node notes from string property to Y.XmlFragment.
 * @param {Function} [progressCallback] - (current, total) => void
 * @returns {{ migrated: boolean, count: number }}
 */
export function migrateNodeNotes(progressCallback = null) {
  if (!docManager) {
    logWarn("Cannot migrate node notes - not initialized");
    return { migrated: false, count: 0 };
  }
  StorageHealth.assertHealthy();
  if (!canMigrate(docManager.docId)) return { migrated: false, count: 0 };
  return docManager.migrateNodeNotes(progressCallback);
}

/**
 * Migrate doc notes content from plain string to Y.XmlFragment (additive).
 * Distinct from the older migrateDocNotes() which moves notes between
 * legacy storage locations into Y.Array. Bridge release: populates
 * fragments without clearing legacy strings.
 * @param {Function} [progressCallback] - (current, total) => void
 * @returns {{ migrated: boolean, count: number }}
 */
export function migrateDocNotesToFragments(progressCallback = null) {
  if (!docManager) {
    logWarn("Cannot migrate doc notes to fragments - not initialized");
    return { migrated: false, count: 0 };
  }
  StorageHealth.assertHealthy();
  if (!canMigrate(docManager.docId)) return { migrated: false, count: 0 };
  return docManager.migrateDocNotesToFragments(progressCallback);
}

/**
 * Get the Y.XmlFragment for a doc note's body (for collaborative editing).
 * Routes through the same _resolveDocNotesManager helper that the
 * existing getDocNotes / addDocNote APIs use, so a notes tab pointed at
 * a linked doc returns that doc's fragment, not the main doc's.
 * @param {string} noteId
 * @param {string} [docId] - Optional target doc; defaults to main.
 * @returns {Y.XmlFragment|null}
 */
export function getDocNoteFragment(noteId, docId) {
  const dm = _resolveDocNotesManager(docId);
  if (!dm) return null;
  return dm.getDocNoteFragment(noteId);
}

/**
 * Serialize a doc note's Y.XmlFragment to a markdown string.
 * @param {string} noteId
 * @param {string} [docId]
 * @returns {string}
 */
export function serializeDocNote(noteId, docId) {
  const dm = _resolveDocNotesManager(docId);
  if (!dm) return '';
  return dm.serializeDocNote(noteId);
}

/**
 * Serialize a doc note's Y.XmlFragment directly to HTML — preserves trailing
 * hardBreaks and empty paragraphs that markdown can't represent.
 * Use this for static display of collab notes.
 * @param {string} noteId
 * @param {string} [docId]
 * @returns {string}
 */
export function serializeDocNoteHtml(noteId, docId) {
  const dm = _resolveDocNotesManager(docId);
  if (!dm) return '';
  return dm.serializeDocNoteHtml(noteId);
}

/**
 * Whether collaborative doc-notes (Y.XmlFragment editing via
 * ySyncPlugin) are available on the given doc — i.e., the migration
 * has run and metadata.docNotesFormat === 'yxml-v1'.
 * @param {string} [docId]
 * @returns {boolean}
 */
export function isCollabDocNotesEnabled(docId) {
  const dm = _resolveDocNotesManager(docId);
  return dm?.metadata?.get('docNotesFormat') === 'yxml-v1';
}


/**
 * Get the Y.XmlFragment for a node's notes (for collaborative editing).
 *
 * Routes across docs: nodes inside a linked doc's composited subtree (and
 * stubs) return fragments from the linked doc's Y.Doc, using the translated
 * id for stubs. This keeps the collaborative editor bound to the correct
 * CRDT so edits flow to the doc that owns the content.
 *
 * @param {string} nodeId
 * @returns {Y.XmlFragment|null}
 */
/** The Store's copy of notes just edited, now rather than at the next pause in typing. */
export { flushNotesRefresh };

export function getNodeNotesFragment(nodeId) {
  const linked = getDocManagerForLinkedNode(nodeId);
  if (linked.linkedDocId && linked.docManager) {
    return linked.docManager.getNodeNotesFragment(linked.translatedId);
  }
  if (!docManager) return null;
  return docManager.getNodeNotesFragment(nodeId);
}

/**
 * A node's note as Markdown as it is now, or null when its map keeps notes
 * in the node itself (the tree has them). The tree's copy of a note an
 * editor changes follows after a pause in typing (store-rebuild.js
 * scheduleNotesRefresh); the engine's notes.read uses this.
 * @param {string} nodeId
 * @returns {string|null}
 */
export function readNodeNotes(nodeId) {
  const linked = getDocManagerForLinkedNode(nodeId);
  const inLinked = !!(linked.linkedDocId && linked.docManager);
  const dm = inLinked ? linked.docManager : docManager;
  const id = inLinked ? linked.translatedId : nodeId;
  if (!dm || dm.metadata?.get('notesFormat') !== 'yxml-v1') return null;
  // As the tree reads them (yjs-doc.js _attachNotes): the live note when it
  // holds something, else the text kept in the node.
  if (dm._notesFragmentIfAny(id)?.length > 0) return dm.serializeNodeNotes(id) || '';
  const flat = dm.getNode(id)?.notes;
  return typeof flat === 'string' ? flat : '';
}

/**
 * Whether an item's note is all Markdown can say (notes-fragment-serializer
 * fragmentIsMarkdownOnly): true for a note kept as text in the item, or
 * none. An interface that edits notes as Markdown edits only these.
 * @param {string} nodeId
 * @returns {boolean}
 */
export function isNodeNoteMarkdownOnly(nodeId) {
  const linked = getDocManagerForLinkedNode(nodeId);
  const inLinked = !!(linked.linkedDocId && linked.docManager);
  const dm = inLinked ? linked.docManager : docManager;
  const id = inLinked ? linked.translatedId : nodeId;
  if (!dm || dm.metadata?.get('notesFormat') !== 'yxml-v1') return true;
  const fragment = dm._notesFragmentIfAny(id);
  if (!fragment || fragment.length === 0) return true;
  // Not known (the serializer not loaded yet): treated as rich, never overwritten.
  try { return fragmentIsMarkdownOnly(fragment); } catch { return false; }
}

/**
 * Get the Y.Doc instance (for y-prosemirror).
 * @returns {Y.Doc|null}
 */
export function getDoc() {
  return docManager?.doc || null;
}

/**
 * Check if collaborative notes (Y.XmlFragment) are enabled for this document.
 * @returns {boolean}
 */
export function isCollabNotesEnabled() {
  return docManager?.metadata?.get('notesFormat') === 'yxml-v1';
}

/**
 * Move the open map to schema v2 (docs/plans/2026-10-02-crdt-schema-v2.md,
 * decision 5: automatically when opened, once synced, by a member who can
 * write). Not as a viewer (the server refuses the write). Whatever the
 * storage quota (Alex, 2026-10-03: every map converted, so the old format's
 * code can go one day): over the quota, the change waits on this device as
 * any other until there is room. The caller takes the backup first.
 * @returns {{migrated: boolean, count: number, skipped?: string}}
 */
export function migrateMapToV2() {
  if (!docManager) return { migrated: false, count: 0, skipped: "no-map" };
  if (docManager.isV2()) return { migrated: false, count: 0 };
  if (getShareRole(docManager.docId) === "viewer") return { migrated: false, count: 0, skipped: "viewer" };
  StorageHealth.assertHealthy();
  return docManager.migrateToV2();
}

/** Nodes in the open map (to decide whether a migration shows progress). */
export function getMapNodeCount() {
  return docManager?.nodeCount() ?? 0;
}

/** Whether the open map is in schema v2. */
export function isMapV2() {
  return !!docManager?.isV2();
}

/**
 * Move the open map back to schema v1 (MapNodesV2.rollbackToV1): only for
 * the rollback procedure, before an older release goes back out
 * (docs/plans/2026-10-02-crdt-schema-v2.md, "Rollback").
 */
export function rollbackMapToV1() {
  if (!docManager) return { rolledBack: false, count: 0 };
  StorageHealth.assertHealthy();
  assertDocQuota(docManager.docId);
  return docManager.rollbackToV1();
}

/**
 * Create a backup of the current doc state (for migration safety).
 * @returns {Uint8Array|null}
 */
export function createDocBackup() {
  if (!docManager) return null;
  return docManager.createBackup();
}

// ============================================
// Tag Definitions
// ============================================

/**
 * Get all tag definitions.
 *
 * With a `nodeId`, returns the tag definitions of the doc that OWNS the
 * node — the host doc, or the linked doc for a stub / linked-doc descendant.
 * Tag IDs are per-doc, so a picker shown on a linked node must pull from
 * the linked doc or the ids won't resolve.
 *
 * Without a `nodeId`, defaults to the main doc (back-compat for UI like the
 * sidebar tag list that isn't tied to a specific node).
 *
 * @param {string} [nodeId]
 * @returns {Array<{id: string, name: string, color: string}>}
 */
export function getTagDefinitions(nodeId) {
  if (nodeId) {
    const activeShare = getActiveShareId();
    if (activeShare) return getSharedDocManager(activeShare)?.getTagDefinitions() || [];
    const routing = getDocManagerForLinkedNode(nodeId);
    if (routing.linkedDocId) {
      const mgr = _resolveDocManagerById(routing.linkedDocId);
      if (mgr) return mgr.getTagDefinitions();
    }
  }
  if (!docManager) return [];
  return docManager.getTagDefinitions();
}

/**
 * Replace the full tag definitions list. Used by JSON import to round-trip tags
 * (definitions plus per-node tag references) from an export.
 *
 * Mandatory tags (Todo/In Progress/Done) are matched by NAME, not id: when an
 * incoming "Todo" arrives, it replaces the existing "Todo" entirely instead of
 * coexisting alongside. Otherwise an import into a fresh doc — which has just
 * created its own mandatory triplet via initDefaultTags — would end up with 6
 * mandatory tags (3 from each side, all named Todo/In Progress/Done) and the
 * dashboard would double-count.
 *
 * @param {Array<{id: string, name: string, color: string, mandatory?: boolean}>} definitions
 */
export function setTagDefinitions(definitions) {
  if (!docManager) return;
  if (!Array.isArray(definitions)) return;
  StorageHealth.assertHealthy();
  assertDocQuota(docManager.docId);
  const existing = docManager.getTagDefinitions() || [];
  const incomingMandatoryNames = new Set(
    definitions.filter(t => t.mandatory).map(t => t.name)
  );
  const incomingIds = new Set(definitions.map(t => t.id));
  // Preserve only existing tags that are NOT shadowed by incoming. For
  // mandatory tags, "shadowed" means same name; for non-mandatory, same id.
  const preserved = existing.filter(t => {
    if (t.mandatory) return !incomingMandatoryNames.has(t.name);
    return !incomingIds.has(t.id);
  });
  docManager.setTagDefinitions([...definitions, ...preserved]);
}

/**
 * Add a new tag definition.
 * @param {{id: string, name: string, color: string}} tag
 */
export function addTagDefinition(tag) {
  if (!docManager) return;
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  const defs = docManager.getTagDefinitions();
  // New user tags go to the default category (Labels) explicitly, so later
  // category features don't have to guess.
  defs.push(tag.mandatory || tag.categoryId ? tag : { ...tag, categoryId: DEFAULT_TAG_CATEGORY_ID });
  docManager.setTagDefinitions(defs);
}

/**
 * Add any missing Todo / In Progress / Done definition to the displayed map.
 * For maps without server sync only (local / logged-out): synced maps get the
 * same from migrateDocNotes() after their first sync — never before it, since
 * a pre-sync write would race the server's tag list. These three can't be
 * deleted or renamed in the app, so a missing one was never seeded (e.g. an
 * imported file without tag definitions), not removed on purpose.
 */
export function ensureDefaultTags() {
  if (!docManager || !canMigrate(docManager.docId)) return;
  try {
    StorageHealth.assertHealthy();
    docManager.initDefaultTags(generateNodeId);
  } catch (err) {
    logWarn("ensureDefaultTags skipped:", err?.message || err);
  }
}

/** Stored tag categories of the displayed map (see engine/core/tag-filter.js). */
export function getTagCategories() {
  return docManager?.getTagCategories?.() || [];
}

/**
 * Update an existing tag definition.
 * @param {string} tagId
 * @param {Object} updates - { name?, color? }
 */
export function updateTagDefinition(tagId, updates) {
  if (!docManager) return;
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  const defs = docManager.getTagDefinitions();
  const idx = defs.findIndex(t => t.id === tagId);
  if (idx === -1) return;
  defs[idx] = { ...defs[idx], ...updates };
  docManager.setTagDefinitions(defs);
}

/**
 * Delete a tag definition and remove it from all nodes.
 * Refuses to delete mandatory tags (Todo, In Progress, Done).
 * @param {string} tagId
 */
export function deleteTagDefinition(tagId) {
  if (!docManager) return;
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  const defs = docManager.getTagDefinitions();
  const def = defs.find(t => t.id === tagId);
  if (def?.mandatory) return; // Cannot delete mandatory tags
  docManager.doc.transact(() => {
    docManager.metadata.set('tagDefinitions', defs.filter(t => t.id !== tagId));
    docManager.forEachNode((flatNode, nodeId) => {
      const has = flatNode.tags?.some(t =>
        typeof t === 'string' ? t === tagId : t.tagId === tagId
      );
      if (has) {
        const newTags = flatNode.tags.filter(t =>
          typeof t === 'string' ? t !== tagId : t.tagId !== tagId
        );
        docManager.updateNode(nodeId, { tags: newTags.length ? newTags : null }, USER_ORIGIN);
      }
    });
  }, USER_ORIGIN);
}

/**
 * Check if a node has a given tag.
 * Handles both old format (string[]) and new format ([{tagId, setAt}]).
 */
export function nodeHasTag(node, tagId) {
  if (!node?.tags?.length) return false;
  return node.tags.some(t => typeof t === 'string' ? t === tagId : t.tagId === tagId);
}

/**
 * Get the tag ID from a tag entry (handles both formats).
 */
export function getTagId(tagEntry) {
  return typeof tagEntry === 'string' ? tagEntry : tagEntry.tagId;
}

/**
 * Get the mandatory tag ID for a given name (Todo, In Progress, Done).
 * Cached per call since tag definitions rarely change.
 */
function getMandatoryTagId(name, nodeId) {
  const owner = nodeId ? getDocManagerForLinkedNode(nodeId).docManager : docManager;
  const defs = owner?.getTagDefinitions() || [];
  const tag = defs.find(t => t.name === name && t.mandatory);
  return tag?.id || null;
}

/**
 * Public accessor for the Done mandatory tag id (or null if the doc has no
 * tag definitions yet — pre-migration, or fresh-load before init).
 *
 * Used by views to detect when the user has hidden the Done tag in the
 * sidebar, in order to cascade-hide whole "completed" subtrees instead of
 * applying the standard ancestor-aware visibility (which would keep a Done
 * parent visible whenever any of its descendants weren't done themselves).
 */
export function getDoneTagId(nodeId) {
  return getMandatoryTagId('Done', nodeId);
}

/**
 * Check if a node is "done" — has the Done tag or legacy status "done".
 */
export function nodeIsDone(node) {
  if (!node) return false;
  return statusFromTags(node, getTagDefinitions(node.id)) === 'done';
}

/**
 * Check if a node is "in progress" — has the In Progress tag or legacy status.
 */
export function nodeIsInProgress(node) {
  if (!node) return false;
  return statusFromTags(node, getTagDefinitions(node.id)) === 'in-progress';
}

/**
 * Derive the effective status string from tags (for backward-compatible display).
 * Returns 'done', 'in-progress', or 'todo'.
 */
export function getEffectiveStatus(node) {
  if (nodeIsDone(node)) return 'done';
  if (nodeIsInProgress(node)) return 'in-progress';
  return 'todo';
}

/**
 * Toggle a tag on a node. Adds with timestamp if not present, removes if present.
 * @param {string} nodeId
 * @param {string} tagId
 * @returns {boolean} True if tag was added, false if removed
 */
export function toggleNodeTag(nodeId, tagId) {
  if (!docManager) return false;
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  if (isNodeReadOnly(nodeId)) return false;
  // Route to the doc that OWNS the node's content: linked-doc descendants
  // and stubs live in another Y.Doc under a translated id, and the tagId
  // the picker produced resolves against THAT doc's tag definitions
  // (getTagDefinitions(nodeId) routes the same way). Writing to the main
  // doc's Y.Map silently no-opped for those nodes before.
  const routing = getDocManagerForLinkedNode(nodeId);
  const mgr = routing.docManager;
  if (!mgr) return false;
  const targetId = routing.translatedId;
  const flatNode = mgr.getNode(targetId);
  if (!flatNode) return false;
  const has = nodeHasTag(flatNode, tagId);
  assertDocQuota(mgr.docId);
  const updated = withTag(flatNode, tagId, !has, mgr.getTagDefinitions());
  mgr.updateNode(targetId, changedFields(flatNode, updated), USER_ORIGIN);
  return !has;
}

/**
 * A flat node with one tag set (on) or cleared, or null when unchanged.
 * Mandatory tags (Todo / In Progress / Done) are mutually exclusive, and a
 * legacy `status` follows them.
 */
/**
 * The fields `after` changes on `before`: written alone, so a concurrent
 * edit of another field of the node stays (schema v2, one key per field).
 */
function changedFields(before, after) {
  const fields = {};
  for (const [key, value] of Object.entries(after)) {
    if (JSON.stringify(value) !== JSON.stringify(before[key])) fields[key] = value;
  }
  return fields;
}

function withTag(flatNode, tagId, on, defs) {
  const tags = flatNode.tags || [];
  if (nodeHasTag(flatNode, tagId) === on) return null;
  let newTags;
  if (!on) {
    newTags = tags.filter(t => typeof t === 'string' ? t !== tagId : t.tagId !== tagId);
    if (!newTags.length) newTags = null;
  } else {
    const addedDef = defs.find(t => t.id === tagId);
    let filtered = tags;
    if (addedDef?.mandatory) {
      const mandatoryIds = new Set(defs.filter(t => t.mandatory).map(t => t.id));
      filtered = tags.filter(t => {
        const id = typeof t === 'string' ? t : t.tagId;
        return !mandatoryIds.has(id);
      });
    }
    newTags = [...filtered, { tagId, setAt: new Date().toISOString() }];
  }
  const updated = { ...flatNode, tags: newTags };
  if (flatNode.status && defs.some(t => t.id === tagId && t.mandatory))
    updated.status = statusFromTags({ ...updated, status: 'todo' }, defs);
  return updated;
}

/**
 * Set or clear one tag on several nodes (multi-selection), in a single
 * transaction so one undo reverts it. Tag ids belong to a map: the tag is
 * written in the map that defines it (the first node's map, else the main
 * map — a tag just created in the popup), only to that map's nodes. Nodes of
 * another map and read-only nodes are skipped and counted.
 * @returns {{changed: number, skipped: number}}
 */
export function setNodesTag(nodeIds, tagId, on) {
  if (!docManager || !nodeIds?.length) return { changed: 0, skipped: nodeIds?.length || 0 };
  StorageHealth.assertHealthy();
  const first = getDocManagerForLinkedNode(nodeIds[0]).docManager;
  const owner = [first, docManager].find((m) => m?.getTagDefinitions().some((t) => t.id === tagId));
  if (!owner) return { changed: 0, skipped: nodeIds.length };
  const defs = owner.getTagDefinitions();
  // In a batch (engine.map.batch, a paste…): the change joins it, one save
  // and one undo step with the rest, worked out on top of what the batch
  // already changed on the item (a second tag change must not undo the
  // first). Outside one: written now, as one transaction.
  const buffer = isBatching() ? getBatchBuffer() : null;
  const updates = [];
  let skipped = 0;
  for (const id of nodeIds) {
    const routing = getDocManagerForLinkedNode(id);
    let flat = routing.docManager === owner && !isNodeReadOnly(id) && owner.getNode(routing.translatedId);
    if (!flat) {
      skipped++;
      continue;
    }
    if (buffer) {
      for (const op of buffer) if (op.type === 'update' && op.nodeId === id) flat = { ...flat, ...op.updates };
    }
    const next = withTag(flat, tagId, on, defs);
    if (next) updates.push({ nodeId: routing.translatedId, viewId: id, updates: changedFields(flat, next) });
  }
  if (updates.length && buffer) {
    for (const { viewId, updates: fields } of updates) {
      buffer.push({ type: 'update', nodeId: viewId, updates: fields, oldValues: {}, shareId: null });
    }
  } else if (updates.length) {
    assertDocQuota(owner.docId);
    owner.updateNodesBatch(updates.map(({ nodeId, updates: fields }) => ({ nodeId, updates: fields })), USER_ORIGIN);
  }
  return { changed: updates.length, skipped };
}

// ============================================
// Kanban Columns
// ============================================

/**
 * Get kanban column definitions from CRDT metadata.
 * @returns {Array<{tagId: string, order: number}>}
 */
export function getKanbanColumns() {
  if (!docManager) return [];
  return docManager.metadata.get('kanbanColumns') || [];
}

/**
 * Set kanban column definitions.
 * @param {Array<{tagId: string, order: number}>} columns
 */
export function setKanbanColumns(columns) {
  if (!docManager) return;
  StorageHealth.assertHealthy();
  assertDocQuota(docManager?.docId);
  docManager.doc.transact(() => {
    docManager.metadata.set('kanbanColumns', columns);
  }, USER_ORIGIN);
}

/**
 * Initialize default kanban columns from mandatory tags if none exist.
 */
export function initKanbanColumns() {
  if (!docManager) return;
  StorageHealth.assertHealthy();
  if (!canMigrate(docManager.docId)) return;
  const existing = docManager.metadata.get('kanbanColumns');
  if (existing?.length) return;
  const defs = docManager.getTagDefinitions();
  const mandatory = defs.filter(t => t.mandatory);
  const columns = mandatory.map((t, i) => ({ tagId: t.id, order: i }));
  setKanbanColumns(columns);
}

/**
 * Wait for initial sync to complete
 * @param {number} timeout - Max time to wait in ms (default 10000)
 * @returns {Promise<boolean>} True if sync completed successfully
 */
export async function waitForSync(timeout = 10000) {
  if (!syncManager) {
    logWarn("Cannot wait for sync - not initialized");
    return false;
  }
  return await syncManager.waitForSync(timeout);
}

/**
 * Initialize and wait for sync to complete, returning sync status
 * This is a convenience function that combines startSync + waitForSync
 * @param {CryptoKey} encryptionKey - Document encryption key
 * @param {number} timeout - Max time to wait for sync (default 10000)
 * @returns {Promise<{synced: boolean, hasData: boolean}>}
 */
export async function startSyncAndWait(encryptionKey, timeout = 10000) {
  await startSync(encryptionKey);
  const synced = await waitForSync(timeout);
  const hasCRDTData = hasData();
  logInfo(`Sync complete: synced=${synced}, hasData=${hasCRDTData}`);
  return { synced, hasData: hasCRDTData };
}

/**
 * Clean up all resources
 * @returns {Promise<void>}
 */
export async function destroy() {
  // H3 Fix (PLAN18): Cancel any pending scheduled Store rebuild
  // This prevents post-teardown errors from orphaned rAF callbacks
  cancelScheduledRebuild();

  // G7-L2 Fix (PLAN19): Reset metrics on document switch for per-doc analysis
  resetPerformanceMetrics();

  // Clean up event listeners
  cleanupEventListeners();

  // Clean up CRDT observer for main document
  const mainCleanup = getMainDocObserverCleanup();
  if (mainCleanup) {
    mainCleanup();
    setMainDocObserverCleanup(null);
  }

  // Clear any pending post-sync callbacks (avoid running stale callbacks)
  clearPostSyncCallbacks();

  // Stop all shared doc syncs
  await stopAllSharedDocSync();

  // Stop all linked document syncs
  await stopAllLinkedDocs();
  nodeHostDoc.clear();
  stubContent.clear();

  // Always clear active share
  setActiveShareId(null);

  if (syncManager) {
    await syncManager.disconnect();
    syncManager = null;
  }

  if (docManager) {
    // Unbind UndoManager BEFORE destroying the Y.Doc so its internal Y.Map
    // subscriptions release cleanly.
    undoManager.unbindAll();
    await docManager.destroy();
    docManager = null;
  }

  isEnabled = false;
  syncingDepth = 0; // Full reset on cleanup
  pendingDocNotesUpdate = false; // CX-M4 Fix (PLAN21): Clear pending docNotes flag
  currentEncryptionKey = null; // Clear stored encryption key
  minimalInitDone = false; // Reset minimal init flag

  // Clear debounce timers for shared doc updates (H1: memory leak fix)
  clearAllDebounceTimers();

  // CL-L5 Fix (PLAN21): Clear local dimensions cache
  clearAllLocalDimensions();

  // Reset CRDT-first architecture state (cancel any pending rebuilds)
  cancelScheduledRebuild();
}

/** Remove only the signed-out account's known caches after persistence stops. */
export async function clearAccountIndexedDBCaches(scope) {
  try {
    const cleared = await clearAccountCaches(scope);
    logInfo(`Cleared ${cleared.docs} account caches on logout; local maps retained`);
  } catch (error) {
    // Never fall back to a broad wipe or guess that an active/local map belongs
    // to the account. Retain an unreadable/blocked cache for recovery.
    logWarn('Account cache cleanup incomplete:', error);
  }
  // The backups taken before dropping these maps' old copies are account
  // data too (engine/sync/migration-backups.js).
  await forgetOldCopyBackups(scope?.docIds);
  // So are the titles this device remembers of the account's online maps.
  const { forgetKnownTitles } = await import("./known-online.js");
  forgetKnownTitles(scope || {});
}

// ============================================
// Event Constants for Sync
// ============================================

// Add sync-specific events to the Events object if not present
if (!Events.SYNC_STATE_CHANGED) {
  Events.SYNC_STATE_CHANGED = "sync:stateChanged";
}
if (!Events.SYNC_REMOTE_UPDATE) {
  Events.SYNC_REMOTE_UPDATE = "sync:remoteUpdate";
}
if (!Events.SYNC_ERROR) {
  Events.SYNC_ERROR = "sync:error";
}

// ============================================
// Share Lifecycle Integration
// ============================================
// The share lifecycle (initSharedDoc, stopSharedDocSync, etc.) is now
// managed by share-lifecycle.js. We initialize it with callbacks here.

/**
 * Handle share revocation - called from WebSocket messages via share-lifecycle.js
 * This is a thin wrapper around performShareRevocation for the WebSocket path
 */
async function handleShareRevoked(shareId, info, options) {
  logInfo("Share revoked via WebSocket:", shareId, info);
  await performShareRevocation(shareId, info?.title, { fromWebSocket: true });
}

// ============================================
// Linked Document Management (Reference Nodes)
// ============================================

const linkedDocStops = new Map();

/**
 * Initialize sync for a linked (referenced) document.
 * Creates a separate CRDT + WebSocket channel for the linked doc.
 * @param {string} docId - The document ID to link
 * @param {CryptoKey} dek - Decryption key for the document
 * @param {Object} [options] - Options (role, userId, etc.)
 */
export async function initLinkedDoc(docId, dek, options = {}) {
  await linkedDocStops.get(docId);
  if (linkedDocRegistry.has(docId)) {
    logDebug("Linked doc already initialized:", docId);
    return;
  }

  logInfo("Initializing linked doc:", docId);

  try {
    const linkedDocManager = new YjsDocManager(docId, {
      userId: options.userId,
      watchSize: true,
      onUpdate: () => handleLinkedDocUpdate(docId),
    });

    await linkedDocManager.initPersistence();

    // Same guarantee as CRDTBridge.init: a yxml-v1 linked doc serializes note
    // fragments synchronously inside toTree(), so the code-split serializer
    // chunk must be resident before this doc's first rebuild/composite.
    // Non-fatal on failure — toTree() falls back to cached/flat note strings.
    if (linkedDocManager.metadata.get("notesFormat") === "yxml-v1") {
      try {
        await ensureNotesSerializer();
      } catch (err) {
        logWarn("Notes serializer unavailable for linked doc — note bodies may be stale:", err);
      }
    }

    let linkedSyncManager;
    linkedSyncManager = new SyncManager({
      ...(LinkAccess.isLinkMap(docId) && { linkTicket: () => linkTicketFor(docId) }),
      // Viewer on the linked map: never push local state.
      isReadOnly: () => getShareRole(docId) === "viewer",
      onPermissionDenied: () => refreshDocRoleFromServer(docId),
      onChangesRefused: (info) => noteChangesRefused(docId, info),
      onStateChange: (newState, oldState) => {
        logDebug(`Linked doc ${docId} state: ${oldState} -> ${newState}`);
        _trackLinkedReconnects(docId, newState);
      },
      onError: (error) => {
        logError(`Linked doc ${docId} sync error:`, error);
      },
      onRemoteUpdate: () => handleLinkedDocUpdate(docId),
      // Server "removed" on the linked doc's room — mirror share-view
      // behaviour: stop sync, prompt for recovery, and let SHARE_REVOKED
      // migrate stubs to the recovered docId.
      onUnshared: (info) => handleLinkedDocRevoked(docId, info),
      // AutoUnshared tells the OWNER that the last recipient left. The owner
      // keeps full access, so this must not run the revocation/recovery path
      // (which ends by deleting the original map). Members learn about a real
      // revocation through onUnshared ("removed"/"unshared").
      onAutoUnshared: (info) => logInfo("Linked doc no longer shared with anyone:", docId, info),
      onKeyOutOfSync: async () => {
        const newDek = await refreshDekFromServer(docId, linkedSyncManager, { force: true });
        const entry = linkedDocRegistry.get(docId);
        if (newDek && entry) entry.dek = newDek;
        return newDek;
      },
      onSessionInvalidated: (reason) => {
        if (reason === "permission_changed") refreshDocRoleFromServer(docId);
        if (reason === "dek_rotated") {
          refreshDekFromServer(docId, linkedSyncManager, { force: true })
            .then((newDek) => {
              const entry = linkedDocRegistry.get(docId);
              if (newDek && entry) entry.dek = newDek;
            })
            .catch((err) => logWarn("Linked key refresh after rotation failed:", err));
        }
      },
      // Owner rotated this doc's DEK (happens on every member revocation;
      // rotation.rs broadcasts to the whole room). Without this handler the
      // linked channel kept the old key: incoming updates failed decryption
      // (skipped as "malformed") and our own edits were encrypted under the
      // dead key — unrecoverable once any client compacts. Mirrors the
      // main-channel handleDekRotated + updateEncryptionKey semantics.
      onDekRotated: async () => {
        try {
          if (isShareRevoked(docId)) return;
          // Forced: when this client performed the rotation, the vault already
          // holds the new wrap, and a plain refresh would keep the old key.
          const newDek = await refreshDekFromServer(docId, linkedSyncManager, { force: true });
          if (!newDek) return; // we were revoked, or the key could not be refreshed
          const entry = linkedDocRegistry.get(docId);
          if (entry) entry.dek = newDek;
          // Queued updates are raw Yjs updates, encrypted when sent: they go
          // out under the new key after the reconnect (never dropped).
          await linkedSyncManager.restart();
          logInfo("Linked doc reconnected with rotated DEK:", docId);
        } catch (err) {
          logError(`Linked doc ${docId} dek-rotated handling failed:`, err);
        }
      },
      // Marks the registry entry as "server-verified" once the initial WS
      // sync succeeds. Without this flag the composite walker refuses to
      // splice the linked doc's content into the host tree, even if its
      // YjsDocManager has IndexedDB-cached state — so a recipient whose
      // access was revoked sees a "no access" stub instead of stale
      // content from before the revocation. The composite is rebuilt so
      // the stub flips from loading to loaded the moment auth is verified.
      onInitialSync: () => {
        const entry = linkedDocRegistry.get(docId);
        if (entry) entry.verified = true;
        clearLinkedOffline(docId);
        try { rebuildStoreFromCRDT("linked-verified"); } catch {}
      },
      // Cap retries when the user has lost access. Without this the WS
      // would reconnect every 30 s forever for a doc the server keeps
      // 403'ing — noisy console and a small but real battery drain. After
      // the cap, onAccessDenied marks the doc as access-failed and tears
      // down the in-memory YjsDocManager so the cached IndexedDB state
      // can no longer be served by getLinkedDocTree (verified flag is
      // dropped along with the registry entry). The IDB itself stays so
      // a later "Recover" click in the notification has a source.
      maxAttemptsWithoutSuccess: 3,
      onAccessDenied: async () => {
        // The attempt cap is reached for ANY failure to connect: offline,
        // server restarting, or a real refusal. Only a confirmed loss of
        // access may mark the map "no access"; otherwise keep it, show the
        // cached content read-only and retry later.
        const access = await _probeLinkedDocAccess(docId);
        if (access === "denied") {
          logWarn("Linked doc access denied:", docId);
          _markLinkedDocAccessFailed(docId, "access denied");
          // Tear down the in-memory state — registry entry, observer, undo
          // binding, Y.Doc. Leaves IDB in place for recovery.
          stopLinkedDoc(docId).catch((err) =>
            logWarn("stopLinkedDoc after access-denied failed:", err),
          );
        } else {
          logWarn("Linked doc unreachable; showing cached content read-only:", docId);
          markLinkedOffline(docId);
          _scheduleLinkedRetry(docId);
        }
        try { rebuildStoreFromCRDT("linked-access-denied"); } catch {}
      },
    });

    linkedSyncManager.setEncryptionKey(dek);
    await linkedSyncManager.connect(docId, linkedDocManager);

    // Set up CRDT observer for linked doc
    const observerCleanup = setupCRDTObserver(linkedDocManager, null);

    // Bind the UndoManager too so edits routed into this doc's CRDT enter
    // the undo history. Temporal-Ctrl+Z scans every bound manager's
    // top-of-stack timestamp to pick the newest user edit, regardless of
    // which doc is "active" for the view.
    undoManager.bind(linkedDocManager);

    linkedDocRegistry.set(docId, {
      docManager: linkedDocManager,
      syncManager: linkedSyncManager,
      dek,
      observerCleanup,
      // Flipped to true by onInitialSync above. Composite walker / public
      // accessors gate on this so cached IndexedDB data is never displayed
      // as live content for a doc whose server access we haven't proven.
      verified: false,
    });

    logInfo("Linked doc initialized:", docId);
  } catch (error) {
    logError("Failed to initialize linked doc:", docId, error);
    await stopLinkedDoc(docId);
    throw error;
  }
}

/**
 * Stop sync for a linked document and clean up.
 * @param {string} docId - The document ID to stop
 */
export function stopLinkedDoc(docId) {
  if (linkedDocStops.has(docId)) return linkedDocStops.get(docId);
  const stopping = stopLinkedDocImpl(docId);
  linkedDocStops.set(docId, stopping);
  void stopping.finally(() => {
    if (linkedDocStops.get(docId) === stopping) linkedDocStops.delete(docId);
  }).catch(() => {});
  return stopping;
}

async function stopLinkedDocImpl(docId) {
  const entry = linkedDocRegistry.get(docId);
  if (!entry) return;

  logInfo("Stopping linked doc:", docId);

  if (entry.observerCleanup) {
    entry.observerCleanup();
  }
  if (entry.syncManager) {
    await entry.syncManager.disconnect();
  }
  // Unbind the UndoManager BEFORE destroying the Y.Doc so it can release
  // its Y.Map subscriptions cleanly. Its history is discarded along with
  // the doc — consistent with the main/shared doc lifecycle.
  undoManager.unbind(docId);
  if (entry.docManager) {
    await entry.docManager.destroy();
  }

  linkedDocRegistry.delete(docId);
  clearLinkedOffline(docId);
  clearInterval(linkedReconnectProbes.get(docId));
  linkedReconnectProbes.delete(docId);
  clearTimeout(linkedRetryTimers.get(docId));
  linkedRetryTimers.delete(docId);
}

/**
 * Tear down a linked-doc sync channel AND delete its local IndexedDB so
 * no cached snapshot is left behind. Used by paths where access is
 * definitively gone (e.g., user dismissed the share_revoked notification
 * without recovering, or a recovered copy has already been written into
 * the vault). Compare with `stopLinkedDoc`, which only stops sync but
 * keeps the IDB intact so a later "Recover" click still has a source.
 *
 * @param {string} docId
 */
export async function purgeLinkedDoc(docId) {
  await stopLinkedDoc(docId);
  // Mark access-denied so any stragglers (e.g. an in-flight composite
  // walker) render the no-access stub instead of trying to display the
  // doc whose registry entry we just dropped.
  _markLinkedDocAccessFailed(docId, "purged");
  try {
    await deleteDocCache(docId);
    logInfo("purgeLinkedDoc: local cache cleared for", docId);
  } catch (err) {
    logWarn("purgeLinkedDoc: cache delete failed:", docId, err);
  }
  try { rebuildStoreFromCRDT("linked-purged"); } catch {}
}

/**
 * Stop all linked document sync channels.
 */
export async function stopAllLinkedDocs() {
  for (const docId of [...linkedDocRegistry.keys()]) {
    await stopLinkedDoc(docId);
  }
}

// Tracks in-flight initLinkedDoc attempts so a tree rebuild that runs while
// init is still resolving doesn't kick off a duplicate init for the same doc.
const linkedDocInitInflight = new Set();

// Tracks docIds whose ensureLinkedDocSync threw or whose WS upgrade was
// rejected. Used by the composite to distinguish "still loading" from
// "no permission" so the UI can show distinct affordances (spinner vs
// lock + request-access). Each entry stores the wrappedDEK that was in
// the vault at failure time — VAULT_INDEX_UPDATED only clears this
// marker when the current wrap differs from the recorded one. Without
// that gate, every local vault save (even unrelated ones) would clear
// the marker and ensureLinkedDocSync would retry → fail → set marker →
// retry → fail in a loop, with the stub flickering between "loading"
// and "no-access".
//
// Shape: docId -> { message: string, wrappedDEK: string|null }
const linkedDocAccessFailed = new Map();

/** Capture the current wrappedDEK in the vault for a doc, or null. */
function _currentVaultWrap(docId) {
  return VaultStorage.getDoc?.(docId)?.wrappedDEK || null;
}

/** Mark a linked doc as access-failed, snapshotting the wrap so a later
 *  vault refresh that didn't actually change the wrap doesn't trigger
 *  a retry. */
function _markLinkedDocAccessFailed(docId, message, { gone = false } = {}) {
  linkedDocAccessFailed.set(docId, {
    message: message || "access denied",
    wrappedDEK: _currentVaultWrap(docId),
    gone,
  });
}

/**
 * Whether no account owns the map any more (deleted, or moved to a device):
 * asked as a device that knew it (`known`), so the server creates nothing.
 * 410: gone; 409: another account's map (access can be requested); anything
 * else (ours, or no answer): not known to be gone.
 */
async function _linkedDocGone(docId) {
  try {
    const resp = await fetch("/api/docs", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ doc_id: docId, known: true }),
    });
    return resp.status === 410;
  } catch {
    return false;
  }
}

// Tracks linked-doc revocations the user has been prompted about, so we
// don't pop the modal twice if `removed` arrives again across reconnects
// or via multiple stubs referencing the same linked doc. Cleared on a
// successful re-init (user regained access).
const linkedDocRevocationPrompted = new Set();

/**
 * The linked doc just received a share-revoked message.
 *
 * Critical ordering: we capture the in-memory tree from the linked doc's
 * manager BEFORE teardown, and pass it as `preStopTreeData` to
 * performDocumentRevocation. Without this the recovered doc is EMPTY —
 * performShareRevocation only looks for the doc in shared/main registries
 * and has no hook for linkedDocRegistry, so it can't find the Y.Doc to
 * snapshot.
 */
async function handleLinkedDocRevoked(docId, info) {
  logInfo("Linked doc revoked:", docId, info);

  // 1) Capture state NOW, while the Y.Doc is still alive.
  let preStopTreeData = null;
  const entry = linkedDocRegistry.get(docId);
  if (entry?.docManager) {
    try {
      preStopTreeData = entry.docManager.toTree();
    } catch (err) {
      logWarn("Failed to snapshot linked doc before revocation:", err);
    }
  }

  // 2) Mark access-denied so rebuilds in the meantime render the no-access
  //    stub instead of attempting to resync a forbidden doc.
  _markLinkedDocAccessFailed(docId, "share revoked");

  // 3) Tear down sync (observer, WS, undo binding, Y.Doc). Persistence in
  //    IndexedDB stays, but our in-memory state is gone — that's why we
  //    captured preStopTreeData above.
  try { await stopLinkedDoc(docId); } catch (err) { logWarn("stopLinkedDoc failed during revocation:", err); }

  // Trigger a rebuild so the composite re-evaluates and the stub renders
  // in the no-access state immediately — instead of showing stale content
  // until the next unrelated change would rebuild the store.
  try { rebuildStoreFromCRDT("linked-revoked"); } catch {}

  // 4) Prompt (once per docId).
  if (linkedDocRevocationPrompted.has(docId)) return;
  linkedDocRevocationPrompted.add(docId);

  // Resolve the doc's actual title from the vault — the server can't
  // know titles (they're E2EE) so info.title is usually empty/missing.
  // Fall back to the linkedDocTitle cached on the stub, then a generic
  // "this linked document" string. Wrap in quotes only when we have a
  // real name so the modal copy reads naturally either way.
  const vaultTitle = VaultStorage.getDoc?.(docId)?.title;
  const cachedStubTitle = _findCachedLinkedDocTitle(docId);
  const resolvedTitle = vaultTitle || cachedStubTitle || info?.title || null;
  // Each toast has a text with the title and one without (no title known).
  const toast = (key) => resolvedTitle ? t(key, { name: resolvedTitle }) : t(`${key}Unnamed`);

  // Only pop the recovery prompt if the user is actually viewing a doc
  // that contains the revoked stub. If they're working on something
  // else, the share_revoked notification (delivered via the user-WS,
  // independent of this code path) is enough — they can come back to
  // it later from the notifications panel. Without this gate the modal
  // appears any time the linked-doc WS happens to be open, which is
  // confusing if the user is in an unrelated map.
  const referencesNow = _currentDocReferencesLinkedDoc(docId);
  if (!referencesNow) {
    logInfo(
      "Linked doc revoked but current main doc doesn't reference it; skipping modal:",
      docId,
    );
    return;
  }

  const revoked = (outcome, level, key, duration) =>
    notify({ code: NOTICE.LINKED_MAP_REVOKED, level, message: toast(key), data: { mapId: docId, outcome }, duration });
  const proceed = await ask({
    code: QUESTION.RECOVER_LINKED_MAP,
    data: {
      mapId: docId,
      docName: resolvedTitle || t("sync.linkedRevoked.thisDocument"),
      context: t("sync.linkedRevoked.context"),
      recoverText: t("sync.linkedRevoked.recover"),
      laterText: t("common.later"),
    },
    fallback: false,
  });

  if (!proceed) {
    revoked("deferred", "warning", "sync.linkedRevoked.deferred", 6000);
    return;
  }

  // 5) Run recovery directly with the captured tree — bypasses the
  //    shared/main-only lookup path inside performShareRevocation.
  try {
    const result = await performDocumentRevocation(
      docId,
      resolvedTitle || info?.title,
      /* wasActive */ false,       // user isn't IN the revoked doc (host view)
      { fromWebSocket: true },
      preStopTreeData,
    );
    if (result?.success) {
      revoked("recovered", "success", "sync.linkedRevoked.recovered");
    } else {
      revoked("no-data", "error", "sync.linkedRevoked.noData");
      linkedDocRevocationPrompted.delete(docId);
    }
  } catch (err) {
    logError("Linked doc recovery failed:", docId, err);
    revoked("failed", "error", "sync.linkedRevoked.failed");
    linkedDocRevocationPrompted.delete(docId);
  }
}

/** Walk the current main-doc Store tree looking for a stub that
 *  references the given linkedDocId. Used to gate the linked-doc
 *  revocation modal on the user actually being in a doc that
 *  contains the stub right now. */
function _currentDocReferencesLinkedDoc(linkedDocId) {
  const data = store.get?.("data");
  if (!data) return false;
  const stack = [data];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    if (node.linkedDocId === linkedDocId) return true;
    if (Array.isArray(node.children)) {
      for (const child of node.children) stack.push(child);
    }
  }
  return false;
}

/** Walk the current main-doc Store tree and return the linkedDocTitle
 *  field cached on a stub for the given linkedDocId, if any. The
 *  cached title is what was visible on the stub when this client last
 *  saw the linked doc loaded — the closest thing to a user-recognisable
 *  name when the vault entry has already been cleaned up. */
function _findCachedLinkedDocTitle(linkedDocId) {
  const data = store.get?.("data");
  if (!data) return null;
  const stack = [data];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    if (node.linkedDocId === linkedDocId && node.linkedDocTitle) {
      return node.linkedDocTitle;
    }
    if (Array.isArray(node.children)) {
      for (const child of node.children) stack.push(child);
    }
  }
  return null;
}

/** True if access was previously known to fail for this doc. */
export function isLinkedDocAccessDenied(docId) {
  return linkedDocAccessFailed.has(docId);
}

/** No account owns the linked map any more: nothing to request. */
export function isLinkedDocGone(docId) {
  return !!linkedDocAccessFailed.get(docId)?.gone;
}

let noAccessRebuildScheduled = false;
function _scheduleNoAccessRebuild() {
  if (noAccessRebuildScheduled) return;
  noAccessRebuildScheduled = true;
  setTimeout(() => {
    noAccessRebuildScheduled = false;
    try { rebuildStoreFromCRDT("linked-precheck-no-access"); } catch {}
  }, 0);
}

/** True when every link to `docId` in the view already shows the no-access badge. */
function _viewShowsNoAccess(docId) {
  let shown = true;
  (function walk(node) {
    if (!node || !shown) return;
    if (node.linkedDocId === docId && !["no-access", "pending-access", "gone"].includes(node._linkedState)
        && !node._nestedReference && !node._duplicate && !node._idConflict) {
      shown = false;
    }
    (node.children || []).forEach(walk);
  })(store?.getState?.()?.data);
  return shown;
}

/** True while a linked map is unreachable and shown from its cache. */
export function isLinkedDocOffline(docId) {
  return isLinkedOffline(docId);
}

/**
 * An unreachable (not revoked) linked map can be shown from its local copy
 * when the vault still holds its key and the copy has content.
 */
function _hasOfflineCopy(docId, entry) {
  return !!entry?.docManager
    && isLinkedOffline(docId)
    && !!VaultStorage.getDoc?.(docId)?.wrappedDEK
    && !!entry.docManager.metadata?.get?.("rootId");
}

const LINKED_RETRY_MS = 30_000;
// docId -> timestamp before which ensureLinkedDocSync does not retry.
const linkedRetryAfter = new Map();
// docId -> pending reconnect timer for an unreachable linked map.
const linkedRetryTimers = new Map();
// docId -> access probe interval while a connected channel is down.
const linkedReconnectProbes = new Map();

/**
 * Ask the server whether this user still has access to a linked map.
 * @returns {Promise<"denied"|"granted"|"unknown">} "unknown" when the server
 *   cannot be reached (offline, restart) — never treated as a denial.
 */
async function _probeLinkedDocAccess(docId) {
  try {
    const resp = await fetch(`/api/shares/${encodeURIComponent(docId)}/my-dek`, { credentials: "include" });
    if (resp.status === 404) return "denied";
    if (!resp.ok) return "unknown";
    const body = await resp.json().catch(() => ({}));
    return body?.is_revoked ? "denied" : "granted";
  } catch {
    return "unknown";
  }
}

function _scheduleLinkedRetry(docId) {
  clearTimeout(linkedRetryTimers.get(docId));
  linkedRetryTimers.set(docId, setTimeout(() => {
    linkedRetryTimers.delete(docId);
    const entry = linkedDocRegistry.get(docId);
    if (!entry || !isLinkedOffline(docId)) return;
    entry.syncManager?.restart?.().catch((err) => logWarn("Linked doc retry failed:", docId, err));
  }, LINKED_RETRY_MS));
}

/**
 * A channel that connected once reconnects forever, and a failed reconnect
 * does not change its state again. If the map was revoked while this client
 * was offline, the stale content would stay editable: while the channel is
 * down, ask the server every LINKED_RETRY_MS and run the revocation flow
 * when access is gone.
 */
function _trackLinkedReconnects(docId, state) {
  if (state === ConnectionState.SYNCED || state === ConnectionState.CONNECTED || state === ConnectionState.SYNCING) {
    clearInterval(linkedReconnectProbes.get(docId));
    linkedReconnectProbes.delete(docId);
    return;
  }
  if (state !== ConnectionState.RECONNECTING && state !== ConnectionState.DISCONNECTED) return;
  if (linkedReconnectProbes.has(docId)) return;
  linkedReconnectProbes.set(docId, setInterval(async () => {
    if (!linkedDocRegistry.has(docId)) {
      clearInterval(linkedReconnectProbes.get(docId));
      linkedReconnectProbes.delete(docId);
      return;
    }
    if (await _probeLinkedDocAccess(docId) === "denied") {
      clearInterval(linkedReconnectProbes.get(docId));
      linkedReconnectProbes.delete(docId);
      handleLinkedDocRevoked(docId, { reason: "access lost while disconnected" });
    }
  }, LINKED_RETRY_MS));
}

/**
 * A map moved to this device (engine/services/move-to-device.js): stop its linked
 * view's channel before its online copy is deleted, or the channel would
 * report that access was lost. The view is drawn again from the vault.
 */
export async function stopLinkedChannel(docId) {
  if (!linkedDocRegistry.has(docId) && !linkedDocStops.has(docId)) return;
  await stopLinkedDoc(docId);
  linkedDocAccessFailed.delete(docId);
  try { rebuildStoreFromCRDT("linked-moved-to-device"); } catch {}
}

// A map deleted from the vault while open as a linked map: stop its channel
// and close its storage first, or the cache deletion fails (Tauri) and the
// channel keeps running on a deleted map.
setBeforeDocCacheDelete(async (docId) => {
  if (linkedDocRegistry.has(docId) || linkedDocStops.has(docId)) {
    await stopLinkedDoc(docId);
    try { rebuildStoreFromCRDT("linked-deleted"); } catch {}
  }
});

if (typeof window !== "undefined") {
  // Back online: reconnect unreachable linked maps right away.
  window.addEventListener?.("online", () => {
    for (const [docId, entry] of linkedDocRegistry) {
      if (isLinkedOffline(docId)) entry.syncManager?.restart?.().catch(() => {});
    }
  });
}

/**
 * Forget linked-map access markers and pending retries (on logout): they
 * describe the previous account's access.
 */
export function resetLinkedDocState() {
  linkedDocAccessFailed.clear();
  linkedDocRevocationPrompted.clear();
  linkedRetryAfter.clear();
  for (const timer of linkedRetryTimers.values()) clearTimeout(timer);
  linkedRetryTimers.clear();
  for (const probe of linkedReconnectProbes.values()) clearInterval(probe);
  linkedReconnectProbes.clear();
  resetLinkedOffline();
}

/** Drop the access-denied marker (e.g., after the user is granted access). */
export function clearLinkedDocAccessDenied(docId) {
  linkedDocAccessFailed.delete(docId);
}

/**
 * Fetch the DEK for a doc in the vault and init sync for it as a linked doc.
 * Imports raw DEK bytes into an AES-GCM CryptoKey (same as document-loader).
 * @param {string} docId
 * @param {Object} [options]
 */
export async function ensureLinkedDocSync(docId, options = {}) {
  if (!docId) return;
  // A map linked into itself is shown as a badge; never open a second
  // Y.Doc and channel for the open map.
  if (docId === docManager?.docId) return;
  if (linkedDocRegistry.has(docId)) return;
  if ((linkedRetryAfter.get(docId) || 0) > Date.now()) return;
  // If the doc is already mounted as a shared doc (share-lifecycle owns its
  // Y.Doc + SyncManager), reuse that instead of spinning up a parallel
  // channel. Two Y.Docs for the same docId would share an IndexedDB database
  // and race each other — better to have a single source of truth.
  if (getSharedDocManager(docId)) return;
  if (linkedDocInitInflight.has(docId)) return;
  // Don't re-attempt until something changes (vault role grant clears the
  // marker via clearLinkedDocAccessDenied). Avoids a hot retry loop where
  // every store rebuild tries again.
  if (linkedDocAccessFailed.has(docId)) {
    // Marker may pre-date the current main doc (set during a different doc's
    // session); Store.initialize loaded a raw tree, so a composite must flip
    // "loading" → "no-access". Deferred and only while the view still shows
    // it otherwise: this runs inside a rebuild, and rebuilding from here
    // looped forever with two links to maps without access.
    if (!_viewShowsNoAccess(docId)) _scheduleNoAccessRebuild();
    return;
  }
  linkedDocInitInflight.add(docId);
  try {
    // Fast pre-check: if the server already says this user has no
    // active role on the doc (revoked, or row never existed), skip the
    // expensive WS-attempt-then-give-up dance and mark access-failed
    // immediately. Without this the stub spends the full reconnection
    // backoff (~8 s with maxAttemptsWithoutSuccess=3) in a "loading"
    // state before the UI can offer the request-access affordance.
    // /my-dek returns 404 when there's no active doc_keys row, and
    // returns is_revoked=true when the row exists but is revoked.
    // (A map open by its link has no membership to probe: its socket's
    // ticket says whether the link still works.)
    try {
      const probe = LinkAccess.isLinkMap(docId) ? null : await fetch(`/api/shares/${encodeURIComponent(docId)}/my-dek`, {
        credentials: "include",
      });
      if (!probe) {
        // Checked through the link (link-access.js).
      } else if (probe.status === 404) {
        throw new Error("Doc not found or missing wrappedDEK: " + docId);
      }
      if (probe?.ok) {
        const body = await probe.json().catch(() => ({}));
        if (body?.is_revoked) {
          // Removed, and the map deleted since: it no longer exists
          // (2026-10-26; it said "no access", offering a request).
          const revoked = new Error("Access revoked: " + docId);
          revoked.gone = body.map_gone === true;
          throw revoked;
        }
      }
      // Other statuses (network blip, 5xx) → fall through and let the
      // WS path try; transient errors shouldn't pin a doc as no-access.
    } catch (probeErr) {
      // Re-throw as the same access-denied flow the catch block
      // handles, so we share the marker / log / rebuild logic.
      if (/Doc not found|Access revoked/.test(probeErr?.message || "")) {
        throw probeErr;
      }
      // Non-fatal probe error: continue with normal init.
    }

    const dekBytes = await VaultStorage.getDocDEK(docId);
    const dekKey = await crypto.subtle.importKey(
      "raw", dekBytes, { name: "AES-GCM" }, false, ["encrypt", "decrypt"],
    );
    // Register the user's role on this doc so write-guards in node-operations
    // can block edits on viewer-only linked docs. Owned docs have no explicit
    // role in the vault (absence of role === owner); skip registration for
    // those so getShareRole(docId) returns null and writes are allowed.
    const vaultDoc = VaultStorage.getDoc?.(docId);
    if (vaultDoc?.role && vaultDoc.role !== "owner") {
      setShareRole(docId, vaultDoc.role);
    }
    await initLinkedDoc(docId, dekKey, options);
    // Successful init — drop any stale failure/prompted markers so a
    // future revocation on the same docId can prompt again (rare but
    // possible on re-share).
    linkedDocAccessFailed.delete(docId);
    linkedDocRevocationPrompted.delete(docId);
  } catch (err) {
    // "No DEK in vault" / 404 / revoked mean the user has no access: mark
    // it (cleared by the vault-role listener on a grant). Other failures
    // (network, decode) are transient: retry later instead of pinning the
    // map as "no access" for the rest of the session.
    const isNoAccess = /missing wrappedDEK|Doc not found|Access revoked/.test(err?.message || "");
    if (isNoAccess) {
      // Not revoked and no key: maybe no account owns it any more (deleted,
      // or moved to a device). Then requesting access makes no sense.
      const gone = err?.gone ?? (!/Access revoked/.test(err?.message || "") && await _linkedDocGone(docId));
      _markLinkedDocAccessFailed(docId, err?.message || "access denied", { gone });
      logWarn("ensureLinkedDocSync: no access to linked doc", docId);
    } else {
      linkedRetryAfter.set(docId, Date.now() + LINKED_RETRY_MS);
      logError("ensureLinkedDocSync failed for", docId, err);
    }
    // The composite that triggered this fire-and-forget sync rendered the
    // stub as "loading". Without a follow-up rebuild it would stay there
    // forever — the failure didn't generate a Y.Doc update of its own.
    // Force a rebuild so the no-access state takes effect immediately.
    try { rebuildStoreFromCRDT("linked-init-failed"); } catch {}
  } finally {
    linkedDocInitInflight.delete(docId);
  }
}

/**
 * Walk the main tree for any linkedDocId references, and fire-and-forget
 * init for each not-yet-initialized linked doc. When init finishes,
 * handleLinkedDocUpdate triggers another store rebuild that will splice
 * the freshly-available content into the tree.
 * @param {Object} tree
 */
export function ensureLinkedDocsForTree(tree) {
  if (!tree) return;
  const seen = new Set();
  (function walk(node) {
    if (!node) return;
    if (node.linkedDocId && !seen.has(node.linkedDocId)) {
      seen.add(node.linkedDocId);
      if (!linkedDocRegistry.has(node.linkedDocId) && !linkedDocInitInflight.has(node.linkedDocId)) {
        ensureLinkedDocSync(node.linkedDocId);
      }
    }
    if (node.children) node.children.forEach(walk);
  })(tree);
  lastEnsuredLinkedIds = seen;
  _scheduleLinkedChannelReconcile();
}

// Linked maps referenced by the tree last shown (see ensureLinkedDocsForTree).
let lastEnsuredLinkedIds = new Set();
let linkedReconcileTimer = null;
const LINKED_RECONCILE_DELAY_MS = 1500;

function _scheduleLinkedChannelReconcile() {
  clearTimeout(linkedReconcileTimer);
  linkedReconcileTimer = setTimeout(_reconcileLinkedChannels, LINKED_RECONCILE_DELAY_MS);
}

/**
 * Stop the channels of linked maps that are no longer linked anywhere in
 * the open map — for example after deleting a branch that contained the
 * link — and keep the vault's link index (`linkedIntoDocs`) in step. The
 * delay lets undo, cut/paste and moves settle before a channel is stopped.
 */
function _reconcileLinkedChannels() {
  linkedReconcileTimer = null;
  const dm = docManager;
  if (!dm?.docId || isDocumentLoading()) return;
  const referenced = new Set(lastEnsuredLinkedIds);
  const hostedHere = new Set();
  dm.forEachNode((flat) => {
    if (flat?.linkedDocId) {
      referenced.add(flat.linkedDocId);
      hostedHere.add(flat.linkedDocId);
    }
  });
  for (const docId of [...linkedDocRegistry.keys()]) {
    if (referenced.has(docId)) continue;
    logInfo("Linked map no longer linked here; stopping its channel:", docId);
    stopLinkedDoc(docId).catch((err) => logWarn("stopLinkedDoc after unlink failed:", err));
    VaultStorage.removeLink?.(docId, dm.docId).catch(() => {});
  }
  for (const docId of hostedHere) {
    VaultStorage.recordLink?.(docId, dm.docId).catch(() => {});
  }
}

/**
 * Get the tree data from a linked document.
 * @param {string} docId
 * @returns {Object|null}
 */

/**
 * Get the main document's ID. This is the user-owned workspace doc the
 * client loaded on init — it does NOT follow view switches into shared docs.
 * Use getActiveDocId() for operations that must target whatever doc the user
 * is currently acting on (e.g., attachments).
 * @returns {string|null}
 */
export function getCurrentDocId() {
  return docManager?.docId || null;
}

/**
 * Get the ID of the doc the user is currently acting on. Returns the active
 * share's doc id when viewing a shared doc, otherwise the main doc id.
 * Kept in lockstep with getActiveEncryptionKey so the (docId, dek) pair
 * always belongs to the same doc.
 * @returns {string|null}
 */
export function getActiveDocId() {
  const activeShareId = getActiveShareId();
  if (activeShareId) return activeShareId;
  return docManager?.docId || null;
}

/**
 * Get the main document's encryption key. Same scope as getCurrentDocId:
 * the user-owned workspace doc the client loaded on init. Use
 * getActiveEncryptionKey() for attachment ops that must follow the active
 * view.
 * @returns {CryptoKey|null}
 */
export function getCurrentEncryptionKey() {
  return currentEncryptionKey || null;
}

/**
 * Get the DEK of the doc the user is currently acting on. Under the unified
 * doc_keys model (migrations/20260401000001_unified_doc_keys.sql) every
 * member's doc_keys row wraps the same underlying DEK under their own X25519
 * pubkey, so attachments wrapped by any member are readable by every other
 * member — provided this getter returns the DEK of the *active* doc, not
 * always the main one.
 * @returns {CryptoKey|null}
 */
export function getActiveEncryptionKey() {
  const activeShareId = getActiveShareId();
  if (activeShareId) {
    const mgr = getSharedSyncManager(activeShareId);
    if (mgr?.encryptionKey) return mgr.encryptionKey;
  }
  return currentEncryptionKey || null;
}

export function getLinkedDocTree(docId) {
  const entry = linkedDocRegistry.get(docId);
  // Only return cached content if the WS sync has actually verified this
  // user's server-side access in the current session. IndexedDB-cached
  // state from a prior session of access (since revoked) must NOT appear
  // as if it were live — the recipient would believe they're collaborating
  // while edits silently never propagate. They'll see the no-access stub
  // until either re-grant succeeds or they recover the doc to their vault.
  if (entry?.docManager && entry.verified) return entry.docManager.toTree();
  // Unreachable (not revoked) and still in the vault: show the cached copy,
  // read-only until the server confirms access again.
  if (_hasOfflineCopy(docId, entry)) return entry.docManager.toTree();
  // Fall back to a shared-doc manager if the user happens to have this doc
  // mounted as a share — no need for a duplicate Y.Doc.
  const sharedMgr = getSharedDocManager(docId);
  if (sharedMgr) return sharedMgr.toTree();
  return null;
}

/**
 * Get the doc manager for a linked document (or the shared-doc manager if
 * that's where it's mounted).
 * @param {string} docId
 * @returns {YjsDocManager|null}
 */
export function getLinkedDocManager(docId) {
  return linkedDocRegistry.get(docId)?.docManager
    || getSharedDocManager(docId)
    || null;
}

/**
 * Check if a linked document's content is available (from linked registry
 * or a parallel shared-doc mount).
 *
 * "Available" means we should display its content. A linked-doc registry
 * entry whose WS sync has not yet been verified (or whose access was
 * revoked) is treated as unavailable — see getLinkedDocTree comment.
 * @param {string} docId
 * @returns {boolean}
 */
export function hasLinkedDoc(docId) {
  const entry = linkedDocRegistry.get(docId);
  if (entry?.verified) return true;
  if (_hasOfflineCopy(docId, entry)) return true;
  return !!getSharedDocManager(docId);
}

/**
 * Get all active linked document IDs.
 * @returns {string[]}
 */
export function getActiveLinkedDocs() {
  return [...linkedDocRegistry.keys()];
}

/**
 * Handle update from a linked document — triggers store rebuild of parent.
 * @param {string} docId - The linked document that changed
 */
function handleLinkedDocUpdate(docId) {
  logDebug("Linked doc updated:", docId);
  // Rebuild the main store to pick up linked doc changes.
  // The store rebuild will composite linked doc trees at reference nodes.
  rebuildStoreFromCRDT("remote");
}

// nodeId -> docId of the doc whose Y.Map owns this node's entry. Null host
// means the main doc. For nested-stub support, a stub's host may itself be
// a linked doc (the doc that the PARENT stub points to).
const nodeHostDoc = new Map();

// stubId -> { contentDocId, contentNodeId }. For stubs, content edits
// (name/color/notes/children) target `contentDocId`'s Y.Map using the linked
// root's id (`contentNodeId`). Layout, removal and move still target the host.
const stubContent = new Map();

/**
 * Resolve a YjsDocManager from any docId known to the client, in priority:
 *   - null/main: the main docManager.
 *   - linked doc registry.
 *   - shared doc managers (so a doc already open as a share view is reused
 *     instead of getting a second Y.Doc instance fighting over IndexedDB).
 * @param {string|null} docId
 * @returns {YjsDocManager|null}
 */
function _resolveDocManagerById(docId) {
  if (!docId) return docManager;
  const linkedEntry = linkedDocRegistry.get(docId);
  if (linkedEntry?.docManager) return linkedEntry.docManager;
  return getSharedDocManager(docId) || null;
}

/**
 * Doc id to use for operations that must live *with the node* (attachments,
 * per-doc tag definitions, etc.) rather than follow the active-view context.
 *
 *   - For a stub or a linked-doc descendant: the linked doc's id.
 *   - For a regular node: the active doc id (active share or main).
 *
 * @param {string} nodeId
 * @returns {string|null}
 */
export function getDocIdForNode(nodeId) {
  const routing = getDocManagerForLinkedNode(nodeId);
  if (routing.linkedDocId) return routing.linkedDocId;
  return getActiveDocId();
}

/**
 * Look up the AES-GCM DEK for any doc the user has access to. Used by the
 * notes tab bar so attachments pasted in a linked-doc note land in that
 * linked doc's blob, encrypted under its own DEK.
 * @param {string|null} docId
 * @returns {CryptoKey|null}
 */
export function getEncryptionKeyForDoc(docId) {
  if (!docId || docId === docManager?.docId) return currentEncryptionKey || null;
  const linked = linkedDocRegistry.get(docId);
  if (linked?.dek) return linked.dek;
  const shareMgr = getSharedSyncManager(docId);
  if (shareMgr?.encryptionKey) return shareMgr.encryptionKey;
  return null;
}

/**
 * DEK matching `getDocIdForNode`. Looks up the node's effective doc and
 * returns its AES-GCM CryptoKey. Attachments pasted while editing linked
 * content are then stored in the linked doc's blob, encrypted under the
 * linked doc's DEK — readable by every member of that doc, not of the host.
 *
 * @param {string} nodeId
 * @returns {CryptoKey|null}
 */
export function getEncryptionKeyForNode(nodeId) {
  const routing = getDocManagerForLinkedNode(nodeId);
  if (routing.linkedDocId) {
    const linked = linkedDocRegistry.get(routing.linkedDocId);
    if (linked?.dek) return linked.dek;
    const shareMgr = getSharedSyncManager(routing.linkedDocId);
    if (shareMgr?.encryptionKey) return shareMgr.encryptionKey;
  }
  return getActiveEncryptionKey();
}

/**
 * Resolve the canonical (docId, nodeId) pair for a node in the composited
 * tree. Used by refnote creation so cross-doc refnotes carry enough info
 * to navigate to the target node even if the linked doc isn't currently
 * composited at click time.
 *
 *   - Regular main-doc node → {docId: null, nodeId}
 *   - Regular linked-doc descendant → {docId: linkedDocId, nodeId}
 *   - Top-level stub → {docId: linkedDocId, nodeId: linkedRootId}
 *
 * @param {string} nodeId
 * @returns {{docId: string|null, nodeId: string}}
 */
export function getNodeLocation(nodeId) {
  const routing = getDocManagerForLinkedNode(nodeId);
  if (routing.isStub) {
    return { docId: routing.linkedDocId, nodeId: routing.translatedId };
  }
  if (routing.hostDocId) {
    return { docId: routing.hostDocId, nodeId };
  }
  return { docId: null, nodeId };
}

/**
 * True when the user cannot write to the node's effective target doc —
 * either because they're viewing a shared doc as viewer, or because the
 * node belongs to (or points at) a linked doc on which their vault role
 * is `viewer`, or that map is full (engine/services/map-size.js). Used by UI to
 * hide edit affordances.
 * @param {string} nodeId
 * @returns {boolean}
 */
export function isNodeReadOnly(nodeId) {
  if (isActiveShareReadOnly()) return true;
  const target = nodeTargetDoc(nodeId);
  return !!target && (isDocDeleteBlocked(target) || isMapFull(target));
}

/**
 * True when the node cannot be deleted or cut here: read-only for a reason
 * other than a full map (a full map allows deleting, to make room).
 * @param {string} nodeId
 * @returns {boolean}
 */
export function isNodeDeleteBlocked(nodeId) {
  if (isActiveShareDeleteBlocked()) return true;
  const target = nodeTargetDoc(nodeId);
  return !!target && isDocDeleteBlocked(target);
}

function nodeTargetDoc(nodeId) {
  const routed = getDocManagerForLinkedNode(nodeId);
  return routed.linkedDocId || routed.hostDocId;
}

function isDocDeleteBlocked(docId) {
  return getShareRole(docId) === "viewer" || isDocQuotaBlocked(docId) || isLinkedOffline(docId);
}

/**
 * Resolve write targets for a node in the composited tree.
 *
 *   - Regular main-doc node: host = content = main; translatedId = nodeId.
 *   - Regular linked-doc node: host = content = its host linked doc.
 *   - Stub node: host = doc that owns the stub's Y.Map entry (main or an
 *     ancestor linked doc for nested stubs); content = the doc the stub
 *     points to, translatedId = linked root id in that doc.
 *
 * @param {string} nodeId
 * @returns {{
 *   docManager: YjsDocManager|null,        // content target (back-compat name)
 *   hostManager: YjsDocManager|null,
 *   hostDocId: string|null,
 *   linkedDocId: string|null,              // content doc id when isStub, else host doc id for linked-doc nodes
 *   translatedId: string,                  // id to write with on `docManager` for content ops
 *   isStub: boolean,
 * }}
 */
export function getDocManagerForLinkedNode(nodeId) {
  const hostDocId = nodeHostDoc.get(nodeId) || null;
  const hostManager = _resolveDocManagerById(hostDocId);
  const stub = stubContent.get(nodeId);
  if (stub) {
    const contentManager = _resolveDocManagerById(stub.contentDocId);
    return {
      docManager: contentManager,
      hostManager,
      hostDocId,
      linkedDocId: stub.contentDocId,
      translatedId: stub.contentNodeId || nodeId,
      isStub: true,
    };
  }
  // Non-stub: content and host are the same.
  if (hostDocId) {
    return {
      docManager: hostManager,
      hostManager,
      hostDocId,
      linkedDocId: hostDocId,
      translatedId: nodeId,
      isStub: false,
    };
  }
  return {
    docManager, hostManager: docManager,
    hostDocId: null, linkedDocId: null,
    translatedId: nodeId, isStub: false,
  };
}

/**
 * Rebuild the linked node ownership maps from the current composited tree.
 * Tracks (a) which doc each node's Y.Map entry lives in and (b) for each
 * stub, which doc/node to route content edits to.
 * @param {Object} tree - The composite tree with linked doc content
 */
export function rebuildLinkedNodeOwnership(tree) {
  nodeHostDoc.clear();
  stubContent.clear();
  _walkTreeForLinkedNodes(tree, null);
}

function _walkTreeForLinkedNodes(node, hostDocId) {
  if (!node) return;

  // Non-main-doc nodes record their host for layout/remove/move routing.
  if (hostDocId) {
    nodeHostDoc.set(node.id, hostDocId);
  }

  if (node.linkedDocId) {
    // Nested reference (inside an already-composited linked subtree) or a
    // duplicate — rendered as opaque badge, content not composited. The node
    // itself is still a regular entry in its host doc, so it needs a host
    // record (already set above) but no content-routing entry. Do NOT descend
    // past nested references — they have no children in the composited tree.
    if (node._nestedReference || node._duplicate || node._idConflict) {
      return;
    }

    // Top-level composited stub. Content edits route to the linked doc using
    // the composited root's id. Its children in the composited tree belong
    // to the linked doc.
    stubContent.set(node.id, {
      contentDocId: node.linkedDocId,
      contentNodeId: node._linkedRootId || null,
    });
    const childHost = node.linkedDocId;
    if (node.children) {
      for (const child of node.children) {
        _walkTreeForLinkedNodes(child, childHost);
      }
    }
    return;
  }

  if (node.children) {
    for (const child of node.children) {
      _walkTreeForLinkedNodes(child, hostDocId);
    }
  }
}

/**
 * Get the tree data from a shared doc
 * @param {string} shareId
 * @returns {Object|null}
 */
export function getSharedDocData(shareId) {
  const sharedDocMgr = getSharedDocManager(shareId);
  if (!sharedDocMgr) return null;
  return sharedDocMgr.toTree();
}

// ============================================
// Event Listeners (module-level, to break circular dependencies)
// ============================================

// Listen for revocation requests from notifications module
// This breaks the circular dependency: notifications.js no longer imports from crdt-bridge.js
on(Events.REQUEST_SHARE_REVOCATION, async ({ shareId, title, options }) => {
  await performShareRevocation(shareId, title, options);
});

// When a share is revoked and recovered, rewrite any stubs in the
// currently-loaded main doc that pointed at the old shareId. Other host
// docs get a `pendingStubMigrations` entry on their vault record and are
// migrated by document-loader at next open (see handleDocOpen).
on(Events.SHARE_REVOKED, ({ shareId, recoveredDocId }) => {
  if (!shareId) return;
  // Clean every in-memory reference to the old docId first — the markers
  // are now dead weight, and if a later code path lookups the shareId it
  // should see "not found" rather than a stale failed/prompted entry.
  linkedDocAccessFailed.delete(shareId);
  linkedDocRevocationPrompted.delete(shareId);
  nodeHostDoc.forEach((v, k) => { if (v === shareId) nodeHostDoc.delete(k); });
  for (const [stubId, ref] of stubContent) {
    if (ref.contentDocId === shareId) stubContent.delete(stubId);
  }
  removeShareRole(shareId);

  if (!recoveredDocId || !docManager) return;
  applyStubMigrations().catch((err) => logWarn("Stub migration after revocation failed:", err));
});

/**
 * Point the open map's links at the recovered copy of a revoked map.
 *
 * Sources: the map's queued migrations (`pendingStubMigrations`, written
 * for maps that were closed during the revocation) and every recovered copy
 * in the vault (`recoveredFromShareId`). The second source also covers links
 * made before link tracking existed or whose tracking failed.
 *
 * Writes the link node itself in the open map (routing through updateNode
 * would send `linkedDocId` to the linked content). Queued entries are
 * cleared only after they were applied.
 *
 * Only in a map no one else can open: in a shared map the link would point
 * every member at this account's private copy (they keep the original, or
 * their own copy). There the link stays; queued entries stay too.
 * @returns {Promise<number>} number of links rewritten
 */
export async function applyStubMigrations() {
  const dm = docManager;
  if (!dm?.docId) return 0;
  if (await mayHaveOtherMembers(dm.docId, VaultStorage)) return 0;
  if (docManager !== dm) return 0; // another map opened meanwhile
  const docs = VaultStorage.getIndex?.()?.docs || [];
  const inVault = new Set(docs.map((d) => d.docId));
  const mapping = {};
  for (const d of docs) {
    const oldId = d.recoveredFromShareId;
    if (oldId && !inVault.has(oldId)) mapping[oldId] = d.docId;
  }
  const queued = docs.find((d) => d.docId === dm.docId)?.pendingStubMigrations || {};
  for (const [oldId, newId] of Object.entries(queued)) {
    if (inVault.has(newId)) mapping[oldId] = newId;
  }

  const rewrites = [];
  dm.forEachNode((flat, nodeId) => {
    const newId = flat?.linkedDocId && mapping[flat.linkedDocId];
    if (newId && newId !== dm.docId) rewrites.push({ nodeId, updates: { linkedDocId: newId } });
  });
  if (rewrites.length > 0) {
    logInfo(`Pointing ${rewrites.length} link(s) of ${dm.docId} at recovered maps`);
    dm.updateNodesBatch(rewrites, MIGRATION_ORIGIN);
    for (const { updates } of rewrites) {
      VaultStorage.recordLink?.(updates.linkedDocId, dm.docId).catch(() => {});
    }
  }
  if (Object.keys(queued).length > 0) {
    await VaultStorage.consumePendingStubMigrations?.(dm.docId);
  }
  return rewrites.length;
}

// Keep sharedRoles mirror in sync with the user's vault on every vault
// update. Role changes (promotion/demotion) take effect for the
// write-guards immediately, without waiting for a reconnect. We only set
// or clear roles for docs that are CURRENTLY in the vault — deletions
// are handled by their respective flows (stopLinkedDoc /
// stopSharedDocSync both go through removeShareRole), and we avoid
// wiping the map on a transient empty-vault snapshot during startup.
// Access-request pending-set changed (user submitted a request, or one
// was resolved). Force a composite rebuild so stub badges swap between
// no-access / pending-access states without needing an unrelated edit.
on(Events.ACCESS_REQUESTS_UPDATED, () => {
  try { rebuildStoreFromCRDT("access-requests-updated"); } catch {}
});

on(Events.VAULT_INDEX_UPDATED, () => {
  const index = VaultStorage.getIndex?.();
  if (!index?.docs) return;
  // Keep the open map's Store role (edit affordances) in step with the vault.
  const openDoc = index.docs.find((d) => d.docId === store.get("currentDocId"));
  if (openDoc) store.setShareRole(openDoc.role || "owner");
  const newlyAccessible = [];
  for (const doc of index.docs) {
    if (!doc.docId) continue;
    if (doc._keyAuthorityConflict) {
      reportSyncPause('map', doc.docId, 'conflict');
      const channels = new Set([
        getCurrentDocId() === doc.docId ? syncManager : null,
        linkedDocRegistry.get(doc.docId)?.syncManager,
        getSharedSyncManager(doc.docId),
      ]);
      for (const channel of channels) {
        if (!channel || channel.getState() === ConnectionState.ERROR) continue;
        // Stop sends immediately, including encryption already in flight.
        // Disconnect retains Yjs data and queued edits for reconciliation.
        channel._setState(ConnectionState.ERROR);
        void channel.disconnect().then(
          () => channel._setState(ConnectionState.ERROR),
          error => {
            channel._setState(ConnectionState.ERROR);
            emit(Events.SYNC_ERROR, { error });
          },
        );
      }
      emit(Events.SYNC_ERROR, { error: new Error('Conflicting map key histories. Sync is paused; local data and all keys are retained.') });
      continue;
    }
    if (doc.role && doc.role !== "owner") {
      setShareRole(doc.docId, doc.role);
    } else {
      removeShareRole(doc.docId);
    }
    // Only clear the access-failed marker when the wrappedDEK in the
    // vault has actually changed since the failure. Otherwise every
    // local vault save (even ones unrelated to this doc) would clear
    // the marker, ensureLinkedDocSync would retry, fail again, set the
    // marker, get cleared on the next save — an infinite loop where the
    // stub flickers between "loading" and "no-access". A new wrap is the
    // only signal that something might have actually changed (e.g. the
    // owner re-granted access and the user just auto-accepted, replacing
    // the wrap with one that the server now recognizes).
    const failedEntry = linkedDocAccessFailed.get(doc.docId);
    if (failedEntry) {
      const currentWrap = doc.wrappedDEK || null;
      if (currentWrap !== failedEntry.wrappedDEK) {
        linkedDocAccessFailed.delete(doc.docId);
        newlyAccessible.push(doc.docId);
      }
    }
  }
  // Owner granted access → retry sync immediately so the stub's content
  // appears without the user needing to refresh the page. Each successful
  // init fires handleLinkedDocUpdate → rebuild → composite → content.
  for (const docId of newlyAccessible) {
    ensureLinkedDocSync(docId).catch((err) =>
      logWarn("Retry of ensureLinkedDocSync after vault update failed:", docId, err),
    );
  }
});
